import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from './Header';
import FloatingIcons from './FloatingIcons';
import Notification from './Notification';
import { auth, autoTune, integration, connector } from '../services/api';
import { ProfileData, OptimizationHistoryItem, OptimizationStep, OptimizationConfiguration, TrainingJob, TrainableModelItem, Pagination } from '../types/api';
import { useOptimizationStore } from '../store/optimizationStore';
import { capitalizeError, formatFineTuningDatasetFormatErrorMessage, getErrorMessage } from '../utils/errorUtils';
import { DEFAULT_CONFIGURATOR_MODELS, getConfiguratorModelIds, setConfiguratorModelIds, toggleConfiguratorModel } from '../utils/configuratorModels';
import { stashConnectorPendingConfig } from '../utils/connectorPendingConfig';
import { OPTIMIZATION_COORDINATOR_MISSING_MESSAGE } from '../utils/agentConfiguratorFromOptimization';
import {
  mergeMetaPromptsWithFinalStepDefault,
  normalizeLegacyRagDocIds,
  normalizeReminderPromptPosition,
} from '../types/agentConfigurator';
import { normalizeWorkerIdString } from '../utils/workerAgentIds';
import {
  getGpt5ModelSpec,
  isBaseModelUnsupportedForFineTuning,
  resolveModelsReasoning,
  shouldShowTemperature,
} from '../utils/gpt5ConfiguratorModels';
import {
  parseOptimizationHistoryResponse,
  splitOptimizationHistoryByCategory,
} from '../utils/optimizationHistoryResponse';
import UsageTransactionsPage from './UsageTransactionsPage';
import ConfirmModal from './ConfirmModal';
import { NOTIFICATION_AUTO_HIDE_MS } from '../constants/uiTiming';
import { getFineTuningGroups } from '../utils/fineTuningGroups';
import {
  assertTrainingJobRetrySucceeded,
  canRetryTrainingJob,
  buildJobRetryUiRowKey,
  isJobRetryUiRowActive,
  isActiveJobStatus,
  resolveTrainingJobRecordId,
} from '../utils/trainingJobRetry';
import ProfileOnboarding, { type ProfileOnboardingTab } from './ProfileOnboarding';
import HelpTourButton from './guidedTour/HelpTourButton';
import FineTuningJobProgress from './training/FineTuningJobProgress';
import { resolveFineTuningJobProgressMetrics, enrichTrainingJobRowForDisplay, resolveEffectiveTrainingJobStatus } from '../utils/trainingJobProgress';
import {
  formatOptimizationDatasetDisplay,
  normalizeOptimizationDatasetNames,
} from '../utils/optimizationDatasetName';
import { buildIntegrationAgentsByName } from '../utils/trainingIntegrationAgents';
import { useAuthContext } from '../contexts/AuthContext';
import {
  resolveStoredIntegrationId,
  setStoredSelectedIntegrationId,
} from '../utils/selectedIntegrationStorage';

function ftStr(v: unknown): string {
  return typeof v === 'string' ? v.trim() : '';
}

/** Id в allowlist конфигуратора: уникальный ft:… иначе имя/база (как в API моделей). */
function trainableRowConfiguratorModelId(m: {
  fine_tuned_model_id?: string | null;
  model_name?: string;
  base_model?: string;
}): string {
  return (
    ftStr(m.fine_tuned_model_id) || ftStr(m.model_name) || ftStr(m.base_model)
  );
}

function resolveTrainableAgentId(
  row: TrainableModelItem,
  agentsByName: Record<string, string>
): string {
  const direct = ftStr(row.agent_id);
  if (direct) return direct;
  const name = ftStr(row.agent_name);
  if (name) return agentsByName[name.toLowerCase()] || '';
  return '';
}

function trainableRowHasFineTunedModel(row: TrainableModelItem): boolean {
  if (ftStr(row.fine_tuned_model_id)) return true;
  const modelName = ftStr(row.model_name);
  const baseModel = ftStr(row.base_model);
  return Boolean(modelName && baseModel && modelName !== baseModel);
}

/** Label for Fine-tuning Jobs «Model» column (prefer friendly names over ft:… ids). */
function extractFineTuningModelDisplayName(job: TrainingJob): string {
  const groups = getFineTuningGroups(job);
  for (const g of groups) {
    if (g == null || typeof g !== 'object') continue;
    const row = g as Record<string, unknown>;
    const label =
      ftStr(row.model_name) ||
      ftStr(row.base_model_label) ||
      ftStr(row.base_model) ||
      ftStr((row as { agent_name?: unknown }).agent_name) ||
      ftStr(row.fine_tuned_model_id);
    if (label) return label;
  }
  return ftStr((job as { model_name?: unknown }).model_name);
}

/** Id passed to Connector configurator (OpenAI fine-tuned id when job finished). */
function extractFineTuningConfiguratorModelId(job: TrainingJob): string {
  const groups = getFineTuningGroups(job);
  for (const g of groups) {
    if (g == null || typeof g !== 'object') continue;
    const row = g as Record<string, unknown>;
    const id =
      ftStr(row.fine_tuned_model_id) || ftStr(row.model_name) || ftStr(row.base_model);
    if (id) return id;
  }
  return ftStr((job as { model_name?: unknown }).model_name);
}

type ModelsIntegrationOption = { id: string; url?: string; label?: string };

function integrationHostname(url: string | undefined): string {
  if (!url?.trim()) return '';
  const raw = url.trim();
  try {
    const withProto = /^[a-z][a-z0-9+.-]*:\/\//i.test(raw) ? raw : `https://${raw}`;
    const host = new URL(withProto).hostname;
    return host || raw;
  } catch {
    return raw.replace(/^https?:\/\//i, '').split('/')[0] || raw;
  }
}

function getModelsIntegrationDisplay(integration: ModelsIntegrationOption): {
  primary: string;
  secondary: string;
  searchBlob: string;
} {
  const { id, url, label } = integration;
  const host = integrationHostname(url);
  const primary =
    (label && label.trim()) ||
    host ||
    (id.length > 14 ? `${id.slice(0, 8)}…${id.slice(-4)}` : id);
  const secondary = (url && url.trim()) || id;
  const searchBlob = `${primary}\n${secondary}\n${id}`.toLowerCase();
  return { primary, secondary, searchBlob };
}

/** Muted placeholder for missing values in legacy optimization history */
function HistoryFieldEmpty({ children = 'Not available' }: { children?: React.ReactNode }) {
  return (
    <span className="history-field-empty" title="Not stored for this run (legacy or incomplete save)">
      {children}
    </span>
  );
}

function hasHistoryIterations(value: unknown): boolean {
  if (value == null) return false;
  if (typeof value === 'number') return !Number.isNaN(value);
  if (typeof value === 'string') return value.trim() !== '';
  return false;
}

function renderHistoryIterations(value: unknown): React.ReactNode {
  return hasHistoryIterations(value) ? (value as React.ReactNode) : <HistoryFieldEmpty>—</HistoryFieldEmpty>;
}

function historyAgentModelId(agent: { model?: string; model_name?: string } | null | undefined): string {
  if (!agent) return '';
  return String(agent.model || agent.model_name || '').trim();
}

/** Stored `models_reasoning`, or effective GPT‑5 default when the family uses reasoning instead of temperature. */
function historyReasoningDisplay(agent: { model?: string; model_name?: string; models_reasoning?: unknown } | null | undefined): React.ReactNode {
  if (!agent) return <HistoryFieldEmpty>—</HistoryFieldEmpty>;
  const model = historyAgentModelId(agent);
  const raw = agent.models_reasoning;
  if (typeof raw === 'string' && raw.trim()) return raw.trim();
  if (getGpt5ModelSpec(model)) {
    const eff = resolveModelsReasoning(model, raw);
    if (eff) return eff;
  }
  return <HistoryFieldEmpty>—</HistoryFieldEmpty>;
}

function historyTemperatureDisplay(
  agent: { model?: string; model_name?: string; models_reasoning?: unknown; temperature?: unknown } | null | undefined
): React.ReactNode {
  if (!agent) return <HistoryFieldEmpty>—</HistoryFieldEmpty>;
  const model = historyAgentModelId(agent);
  const mr = resolveModelsReasoning(model, agent.models_reasoning);
  if (!shouldShowTemperature(model, mr)) {
    return <HistoryFieldEmpty>—</HistoryFieldEmpty>;
  }
  const t = agent.temperature;
  if (t !== undefined && t !== null && typeof t === 'number' && Number.isFinite(t)) return t;
  return <HistoryFieldEmpty>—</HistoryFieldEmpty>;
}

function isWorkerAsynchronous(worker: any): boolean {
  const m = worker?.mode;
  return m === 'asynchronous' || m === 'async';
}

function partitionWorkersByMode(workers: any[]): {
  syncWorkers: Array<{ worker: any; index: number }>;
  asyncWorkers: Array<{ worker: any; index: number }>;
} {
  const syncWorkers: Array<{ worker: any; index: number }> = [];
  const asyncWorkers: Array<{ worker: any; index: number }> = [];
  (workers || []).forEach((worker, index) => {
    if (isWorkerAsynchronous(worker)) asyncWorkers.push({ worker, index });
    else syncWorkers.push({ worker, index });
  });
  return { syncWorkers, asyncWorkers };
}

type HistoryWorkerModeVariant = 'initial' | 'integrations' | 'steps';

function HistoryWorkerModeSections({
  workers,
  expandedAgents,
  toggleAgent,
  groupIdPrefix,
  variant,
}: {
  workers: any[];
  expandedAgents: Set<string>;
  toggleAgent: (key: string) => void;
  groupIdPrefix: string;
  variant: HistoryWorkerModeVariant;
}) {
  const { syncWorkers, asyncWorkers } = partitionWorkersByMode(workers);
  const groups: Array<{
    mode: 'sync' | 'async';
    title: string;
    items: Array<{ worker: any; index: number }>;
    accent: string;
  }> = [];
  if (syncWorkers.length) {
    groups.push({
      mode: 'sync',
      title: 'Synchronous Agents',
      items: syncWorkers,
      accent: '#0099FA',
    });
  }
  if (asyncWorkers.length) {
    groups.push({
      mode: 'async',
      title: 'Asynchronous Agents',
      items: asyncWorkers,
      accent: '#0099FA',
    });
  }
  if (groups.length === 0) return null;

  const renderExpanded = (worker: any, originalIndex: number) => {
    if (variant === 'integrations') {
      const promptStr = worker.prompt != null ? String(worker.prompt).trim() : '';
      return (
        <>
          <div className="step-prompt">
            <label>Prompt</label>
            <div
              className={`step-prompt-text formatted-text${!promptStr ? ' step-prompt-text--empty' : ''}`}
            >
              {promptStr ? worker.prompt : <HistoryFieldEmpty>No prompt stored</HistoryFieldEmpty>}
            </div>
          </div>
          <div className="step-params">
            <div className="step-param">
              <label>Model</label>
              <span>
                {worker.model || worker.model_name ? (
                  worker.model || worker.model_name
                ) : (
                  <HistoryFieldEmpty>—</HistoryFieldEmpty>
                )}
              </span>
            </div>
            <div className="step-param">
              <label>Reasoning</label>
              <span>{historyReasoningDisplay(worker)}</span>
            </div>
            <div className="step-param">
              <label>Temperature</label>
              <span>{historyTemperatureDisplay(worker)}</span>
            </div>
            {worker.mode && (
              <div className="step-param">
                <label>Mode</label>
                <span>{worker.mode}</span>
              </div>
            )}
            <div className="step-param">
              <label>Use RAG</label>
              <span>{worker.use_rag ? 'Yes' : 'No'}</span>
            </div>
            <div className="step-param">
              <label>Use LTM</label>
              <span>{worker.use_ltm ? 'Yes' : 'No'}</span>
            </div>
          </div>
        </>
      );
    }
    if (variant === 'steps') {
      return (
        <>
          <div className="step-prompt">
            <label>Prompt</label>
            <div className="step-prompt-text formatted-text">{worker.prompt || 'No prompt available'}</div>
          </div>
          {worker.description && (
            <div className="step-prompt">
              <label>Description</label>
              <div className="step-prompt-text formatted-text">{worker.description}</div>
            </div>
          )}
          <div className="step-params">
            <div className="step-param">
              <label>Model</label>
              <span>{worker.model_name || worker.model || 'Not specified'}</span>
            </div>
            <div className="step-param">
              <label>Reasoning</label>
              <span>{historyReasoningDisplay(worker)}</span>
            </div>
            <div className="step-param">
              <label>Temperature</label>
              <span>{historyTemperatureDisplay(worker)}</span>
            </div>
            <div className="step-param">
              <label>Mode</label>
              <span>{worker.mode || 'synchronous'}</span>
            </div>
            <div className="step-param">
              <label>Use RAG</label>
              <span>{worker.use_rag ? 'Yes' : 'No'}</span>
            </div>
            <div className="step-param">
              <label>Use LTM</label>
              <span>{worker.use_ltm ? 'Yes' : 'No'}</span>
            </div>
          </div>
        </>
      );
    }
    return (
      <>
        <div className="step-prompt">
          <label>Prompt</label>
          <div
            className={`step-prompt-text formatted-text${!(worker.prompt && String(worker.prompt).trim()) ? ' step-prompt-text--empty' : ''}`}
          >
            {worker.prompt && String(worker.prompt).trim() ? (
              worker.prompt
            ) : (
              <HistoryFieldEmpty>No prompt stored</HistoryFieldEmpty>
            )}
          </div>
        </div>
        {worker.description && (
          <div className="step-prompt">
            <label>Description</label>
            <div className="step-prompt-text formatted-text">{worker.description}</div>
          </div>
        )}
        <div className="step-params">
          <div className="step-param">
            <label>Model</label>
            <span>
              {worker.model_name || worker.model ? (
                worker.model_name || worker.model
              ) : (
                <HistoryFieldEmpty>—</HistoryFieldEmpty>
              )}
            </span>
          </div>
          <div className="step-param">
            <label>Reasoning</label>
            <span>{historyReasoningDisplay(worker)}</span>
          </div>
          <div className="step-param">
            <label>Temperature</label>
            <span>{historyTemperatureDisplay(worker)}</span>
          </div>
          <div className="step-param">
            <label>Mode</label>
            <span>{worker.mode || 'synchronous'}</span>
          </div>
          <div className="step-param">
            <label>Use RAG</label>
            <span>{worker.use_rag ? 'Yes' : 'No'}</span>
          </div>
          <div className="step-param">
            <label>Use LTM</label>
            <span>{worker.use_ltm ? 'Yes' : 'No'}</span>
          </div>
        </div>
      </>
    );
  };

  return (
    <>
      {groups.map(({ mode, title, items, accent }) => {
        const groupKey = `${groupIdPrefix}-workers-${mode}`;
        const modeClass =
          mode === 'sync' ? 'history-worker-mode-section--sync' : 'history-worker-mode-section--async';
        return (
          <div
            key={groupKey}
            className={`agent-section workers-section history-worker-mode-section ${modeClass}`}
          >
            <div
              className="agent-section-header history-worker-mode-section__header"
              onClick={() => toggleAgent(groupKey)}
              style={{ cursor: 'pointer' }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <i
                  className={`fas fa-chevron-${expandedAgents.has(groupKey) ? 'down' : 'right'}`}
                  style={{
                    fontSize: '11px',
                    color: 'var(--accent-blue)',
                    transition: 'transform 0.2s ease',
                  }}
                />
                <i className="fas fa-users" style={{ color: accent, fontSize: '14px' }} />
                <span className="agent-section-title history-worker-mode-section__title">
                  {title} ({items.length})
                </span>
              </div>
            </div>
            {expandedAgents.has(groupKey) && (
              <div className="agent-section-content">
                {items.map(({ worker, index }) => {
                  const workerKey = `${groupIdPrefix}-worker-${index}`;
                  const isWorkerExpanded = expandedAgents.has(workerKey);
                  return (
                    <div key={workerKey} className="worker-agent-item">
                      <div
                        className="worker-agent-header"
                        onClick={() => toggleAgent(workerKey)}
                        style={{ cursor: 'pointer', marginBottom: '12px' }}
                      >
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                          <i
                            className={`fas fa-chevron-${isWorkerExpanded ? 'down' : 'right'}`}
                            style={{
                              fontSize: '10px',
                              color: 'var(--accent-blue)',
                              transition: 'transform 0.2s ease',
                            }}
                          />
                          <i className="fas fa-robot" style={{ color: 'var(--accent-blue)', fontSize: '13px' }} />
                          <span className="worker-agent-name">
                            Worker {index + 1}: {worker.name || `Worker ${index + 1}`}
                          </span>
                        </div>
                      </div>
                      {isWorkerExpanded && (
                        <div className="worker-agent-content">{renderExpanded(worker, index)}</div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}
    </>
  );
}

const OPTIMIZATION_HISTORY_PAGE_SIZE = 20;

export default function ProfilePage() {
  const NAME_MAX_LENGTH = 255;
  const COMPANY_MAX_LENGTH = 255;

  const navigate = useNavigate();
  const { user } = useAuthContext();
  const [tab, setTab] = useState<'profile' | 'history' | 'usage' | 'models' | 'fine-tuning'>('profile');
  const [showProfileOnboarding, setShowProfileOnboarding] = useState(false);
  const [name, setName] = useState('');
  const [company, setCompany] = useState('');
  const [comm, setComm] = useState(false);
  const [nameRequiredError, setNameRequiredError] = useState(false);
  const [showSaved, setShowSaved] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [history, setHistory] = useState<OptimizationHistoryItem[]>([]);
  const [multiAgentHistory, setMultiAgentHistory] = useState<OptimizationHistoryItem[]>([]);
  const [integrationsHistory, setIntegrationsHistory] = useState<OptimizationHistoryItem[]>([]);
  const [historyCategory, setHistoryCategory] = useState<'multi-agent' | 'integrations'>('integrations');
  const [isHistoryLoading, setIsHistoryLoading] = useState(false);
  const [historyLoaded, setHistoryLoaded] = useState(false);
  const [historyPagination, setHistoryPagination] = useState<Pagination | null>(null);
  const [historyLoadingMore, setHistoryLoadingMore] = useState(false);
  const [allHistoryItems, setAllHistoryItems] = useState<OptimizationHistoryItem[]>([]);
  const [showStepsModal, setShowStepsModal] = useState(false);
  const [showConfigModal, setShowConfigModal] = useState(false);
  const [selectedSteps, setSelectedSteps] = useState<OptimizationHistoryItem['steps']>([]);
  const [selectedConfig, setSelectedConfig] = useState<OptimizationHistoryItem['configuration'] | null>(null);
  const [selectedConfigSteps, setSelectedConfigSteps] = useState<OptimizationHistoryItem['steps'] | null>(null);
  const [selectedStepId, setSelectedStepId] = useState<number | null>(null);
  const [selectedRunId, setSelectedRunId] = useState<string | null>(null);
  const [selectedIntegrationId, setSelectedIntegrationId] = useState<string | null>(null);
  /** Run-level config name from API (`config_label`); not part of `configuration` JSON. */
  const [selectedHistoryRunConfigLabel, setSelectedHistoryRunConfigLabel] = useState<string | null>(null);
  const [expandedSteps, setExpandedSteps] = useState<Set<number>>(new Set());
  const [expandedAgents, setExpandedAgents] = useState<Set<string>>(new Set());
  const [saveClicked, setSaveClicked] = useState(false);
  const [hasNameLimitWarning, setHasNameLimitWarning] = useState(false);
  const [hasCompanyLimitWarning, setHasCompanyLimitWarning] = useState(false);

  // Models tab (trainable models → configurator selection)
  const [modelsIntegrations, setModelsIntegrations] = useState<ModelsIntegrationOption[]>([]);
  const [modelsSelectedIntegrationId, setModelsSelectedIntegrationId] = useState<string | null>(null);
  const [modelsIntegrationPickerOpen, setModelsIntegrationPickerOpen] = useState(false);
  const [modelsIntegrationSearch, setModelsIntegrationSearch] = useState('');
  const modelsIntegrationPickerRef = useRef<HTMLDivElement>(null);
  const [trainableModels, setTrainableModels] = useState<TrainableModelItem[]>([]);
  const [integrationAgentsByName, setIntegrationAgentsByName] = useState<Record<string, string>>({});
  const [deletingTrainingAgentModelId, setDeletingTrainingAgentModelId] = useState<string | null>(null);
  const [deleteModelConfirmTarget, setDeleteModelConfirmTarget] = useState<TrainableModelItem | null>(null);
  const [modelsLoading, setModelsLoading] = useState(false);
  const [modelsError, setModelsError] = useState<string | null>(null);
  const [modelsConfiguratorIds, setModelsConfiguratorIds] = useState<string[]>([]);
  const [fineTuningHistory, setFineTuningHistory] = useState<TrainingJob[]>([]);
  const [fineTuningLoading, setFineTuningLoading] = useState(false);
  const [fineTuningError, setFineTuningError] = useState<string | null>(null);
  const [fineTuningLoaded, setFineTuningLoaded] = useState(false);
  const [fineTuningPagination, setFineTuningPagination] = useState<{ limit: number; offset: number; total: number } | null>(null);
  const [fineTuningLoadingMore, setFineTuningLoadingMore] = useState(false);
  const [fineTuningSearch, setFineTuningSearch] = useState('');
  const [fineTuningStatusFilter, setFineTuningStatusFilter] = useState('');
  const [fineTuningIntegrationFilter, setFineTuningIntegrationFilter] = useState('');
  const [fineTuningEndReached, setFineTuningEndReached] = useState(false);
  const [integrationNameById, setIntegrationNameById] = useState<Record<string, string>>({});
  const [selectedFineTuningJob, setSelectedFineTuningJob] = useState<TrainingJob | null>(null);
  const [fineTuningRetryingRowKey, setFineTuningRetryingRowKey] = useState<string | null>(null);
  const [fineTuningRetryError, setFineTuningRetryError] = useState<string | null>(null);
  
  // Унифицированная система уведомлений
  const [notification, setNotification] = useState<{
    isVisible: boolean;
    type: 'success' | 'error' | 'info';
    message: string;
  }>({
    isVisible: false,
    type: 'success',
    message: ''
  });
  
  const saveButtonRef = useRef<HTMLButtonElement>(null);
  const nameInputRef = useRef<HTMLInputElement>(null);

  const handleProfileOnboardingTab = useCallback((t: ProfileOnboardingTab) => {
    setTab(t);
  }, []);

  const currentHistory = historyCategory === 'multi-agent' ? multiAgentHistory : integrationsHistory;
  const isHistoryEmpty = !isHistoryLoading && (!Array.isArray(currentHistory) || currentHistory.length === 0);
  const historyHasMore =
    historyPagination != null && allHistoryItems.length < historyPagination.total;

  const modelsIntegrationSearchQ = modelsIntegrationSearch.trim().toLowerCase();
  const filteredModelsIntegrations = useMemo(() => {
    if (!modelsIntegrationSearchQ) return modelsIntegrations;
    return modelsIntegrations.filter((i) =>
      getModelsIntegrationDisplay(i).searchBlob.includes(modelsIntegrationSearchQ)
    );
  }, [modelsIntegrations, modelsIntegrationSearchQ]);

  const selectedModelsIntegration = useMemo(
    () => modelsIntegrations.find((i) => i.id === modelsSelectedIntegrationId) || null,
    [modelsIntegrations, modelsSelectedIntegrationId]
  );
  const selectedModelsIntegrationDisplay = selectedModelsIntegration
    ? getModelsIntegrationDisplay(selectedModelsIntegration)
    : null;

  const normalizeOptimizationSteps = (
    steps: OptimizationHistoryItem['steps'] | null | undefined
  ): OptimizationHistoryItem['steps'] => {
    if (!steps || !Array.isArray(steps) || steps.length === 0) return [];

    const safeTime = (ts?: string) => {
      if (!ts) return 0;
      const parsed = Date.parse(ts);
      return Number.isFinite(parsed) ? parsed : 0;
    };

    const score = (s: OptimizationHistoryItem['steps'][number]) => {
      let val = 0;
      if (s.prompt && s.prompt.trim()) val += 3;
      if (s.model_name && s.model_name.trim()) val += 3;
      // temperature is always present by type, but backend might send 0/undefined-ish values.
      if (typeof s.temperature === 'number' && Number.isFinite(s.temperature)) val += 1;
      if (s.embedding_point) val += 1;
      if (s.evaluation_point) val += 1;
      // Учитываем наличие данных в step.data
      if (extractCoordinatorFromData(s.data)) val += 2;
      return val;
    };

    const pickBetter = (
      a: OptimizationHistoryItem['steps'][number],
      b: OptimizationHistoryItem['steps'][number]
    ) => {
      const sa = score(a);
      const sb = score(b);
      if (sb !== sa) return sb > sa ? b : a;
      return safeTime(b.timestamp) > safeTime(a.timestamp) ? b : a;
    };

    const byStep = new Map<number, OptimizationHistoryItem['steps'][number]>();
    for (const step of steps) {
      const prev = byStep.get(step.step_number);
      byStep.set(step.step_number, prev ? pickBetter(prev, step) : step);
    }

    return Array.from(byStep.values()).sort((a, b) => a.step_number - b.step_number);
  };

  const unwrapStepData = (data: any): any => {
    if (!data || typeof data !== 'object') return {};
    // Legacy runs may nest payload as { data: { ...agents... } }.
    return (data.data && typeof data.data === 'object') ? data.data : data;
  };

  const extractCoordinatorFromData = (data: any): any => {
    const unwrapped = unwrapStepData(data);
    return (
      unwrapped?.["master-agent"] ||
      unwrapped?.master_agent ||
      unwrapped?.coordinator ||
      null
    );
  };

  const extractWorkersFromData = (data: any): any[] => {
    const unwrapped = unwrapStepData(data);
    const workers = unwrapped?.["worker-agents"] || unwrapped?.worker_agents || unwrapped?.workers;
    return Array.isArray(workers) ? workers : [];
  };

  const extractMetaPromptsFromData = (data: any): Record<string, any> | undefined => {
    const unwrapped = unwrapStepData(data);
    return unwrapped?.metaPrompts || unwrapped?.meta_prompts || undefined;
  };

  const extractCoordinatorFromConfig = (config: any): any => {
    if (!config || typeof config !== 'object') return null;
    return config?.["master-agent"] || config?.master_agent || config?.coordinator || null;
  };

  const extractWorkersFromConfig = (config: any): any[] => {
    if (!config || typeof config !== 'object') return [];
    const workers = config?.["worker-agents"] || config?.worker_agents || config?.workers;
    return Array.isArray(workers) ? workers : [];
  };

  // Загрузка профиля при монтировании компонента
  useEffect(() => {
    loadProfile();
  }, []);

  // Загрузка истории при переключении на вкладку истории (только один раз)
  useEffect(() => {
    if (tab === 'history' && !historyLoaded) {
      loadHistory();
    }
  }, [tab, historyLoaded]);

  // Models tab: load integrations when tab is opened
  useEffect(() => {
    if (tab !== 'models') return;
    let cancelled = false;
    (async () => {
      try {
        const resp = await connector.listMyIntegrations();
        const data = resp?.data ?? resp?.items ?? resp ?? [];
        const list = Array.isArray(data) ? data : [];
        if (!cancelled) {
          setModelsIntegrations(
            list.map((i: any) => ({
              id: String(i.id || i._id),
              url: typeof i.url === 'string' ? i.url : undefined,
              label:
                typeof i.label === 'string' && i.label.trim()
                  ? i.label.trim()
                  : typeof i.name === 'string' && i.name.trim()
                    ? i.name.trim()
                    : undefined,
            }))
          );
          if (list.length > 0 && !modelsSelectedIntegrationId) {
            const storedId = resolveStoredIntegrationId('connector', user?.id, list);
            setModelsSelectedIntegrationId(
              storedId || String(list[0].id || list[0]._id)
            );
          }
        }
      } catch {
        if (!cancelled) setModelsIntegrations([]);
      }
    })();
    return () => { cancelled = true; };
  }, [tab]);

  useEffect(() => {
    if (tab !== 'models') {
      setModelsIntegrationPickerOpen(false);
      setModelsIntegrationSearch('');
    }
  }, [tab]);

  useEffect(() => {
    if (tab === 'fine-tuning' && !fineTuningLoaded) {
      void loadFineTuningHistory();
      void loadIntegrationsMapForFineTuning();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, fineTuningLoaded]);

  useEffect(() => {
    if (!modelsIntegrationPickerOpen) return;
    const onDoc = (e: MouseEvent) => {
      if (!modelsIntegrationPickerRef.current?.contains(e.target as Node)) {
        setModelsIntegrationPickerOpen(false);
      }
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [modelsIntegrationPickerOpen]);

  useEffect(() => {
    if (!modelsIntegrationPickerOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setModelsIntegrationPickerOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [modelsIntegrationPickerOpen]);

  // Models tab: load trainable models when integration is selected
  useEffect(() => {
    if (tab !== 'models' || !modelsSelectedIntegrationId) {
      setTrainableModels([]);
      setIntegrationAgentsByName({});
      return;
    }
    let cancelled = false;
    setModelsLoading(true);
    setModelsError(null);
    Promise.all([
      connector.listTrainableModels({ integration_id: modelsSelectedIntegrationId, limit: 200 }),
      connector.listIntegrationAgents(modelsSelectedIntegrationId),
    ])
      .then(([trainableResp, agentsResp]) => {
        if (cancelled) return;
        const items = trainableResp?.data?.items ?? trainableResp?.data ?? [];
        const list = Array.isArray(items) ? items : [];
        setTrainableModels(
          list.filter((row: TrainableModelItem) => !isBaseModelUnsupportedForFineTuning(row?.base_model))
        );
        setIntegrationAgentsByName(buildIntegrationAgentsByName(agentsResp?.data));
      })
      .catch((e) => {
        if (!cancelled) {
          setModelsError(getErrorMessage(e, 'Failed to load trainable models'));
          setTrainableModels([]);
          setIntegrationAgentsByName({});
        }
      })
      .finally(() => {
        if (!cancelled) setModelsLoading(false);
      });
    return () => { cancelled = true; };
  }, [tab, modelsSelectedIntegrationId]);

  const confirmDeleteTrainingAgentModel = useCallback(async () => {
    const row = deleteModelConfirmTarget;
    if (!row) return;

    const agentId = resolveTrainableAgentId(row, integrationAgentsByName);
    if (!agentId) {
      setNotification({
        isVisible: true,
        type: 'error',
        message: 'Agent ID is missing for this model.',
      });
      setDeleteModelConfirmTarget(null);
      return;
    }

    setDeletingTrainingAgentModelId(agentId);
    try {
      const resp = await connector.deleteTrainingAgentModel(agentId);
      if (!resp.success) {
        throw new Error(resp.error?.message || 'Failed to delete fine-tuned model.');
      }

      const modelId = trainableRowConfiguratorModelId(row);
      if (modelsSelectedIntegrationId && modelId) {
        toggleConfiguratorModel(modelsSelectedIntegrationId, modelId, false);
        setModelsConfiguratorIds((prev) => prev.filter((id) => id !== modelId));
      }

      if (modelsSelectedIntegrationId) {
        const [trainableResp, agentsResp] = await Promise.all([
          connector.listTrainableModels({ integration_id: modelsSelectedIntegrationId, limit: 200 }),
          connector.listIntegrationAgents(modelsSelectedIntegrationId),
        ]);
        const items = trainableResp?.data?.items ?? trainableResp?.data ?? [];
        const list = Array.isArray(items) ? items : [];
        setTrainableModels(
          list.filter((item: TrainableModelItem) => !isBaseModelUnsupportedForFineTuning(item?.base_model))
        );
        setIntegrationAgentsByName(buildIntegrationAgentsByName(agentsResp?.data));
      } else {
        setTrainableModels((prev) =>
          prev.filter((item) => resolveTrainableAgentId(item, integrationAgentsByName) !== agentId)
        );
      }

      setDeleteModelConfirmTarget(null);
      setNotification({
        isVisible: true,
        type: 'success',
        message: 'Fine-tuned model deleted.',
      });
    } catch (e) {
      setNotification({
        isVisible: true,
        type: 'error',
        message: getErrorMessage(e, 'Failed to delete fine-tuned model'),
      });
    } finally {
      setDeletingTrainingAgentModelId(null);
    }
  }, [deleteModelConfirmTarget, integrationAgentsByName, modelsSelectedIntegrationId]);

  const handleDeleteTrainingAgentModel = useCallback((row: TrainableModelItem) => {
    setDeleteModelConfirmTarget(row);
  }, []);

  // Sync configurator selection from localStorage when integration changes
  useEffect(() => {
    if (modelsSelectedIntegrationId) {
      setModelsConfiguratorIds(getConfiguratorModelIds(modelsSelectedIntegrationId));
    } else {
      setModelsConfiguratorIds([]);
    }
  }, [modelsSelectedIntegrationId, tab]);

  // Обновляем отображаемую историю при изменении категории
  useEffect(() => {
    setHistory(historyCategory === 'multi-agent' ? multiAgentHistory : integrationsHistory);
  }, [historyCategory, multiAgentHistory, integrationsHistory]);

  useEffect(() => {
    if (!showConfigModal) {
      setSelectedHistoryRunConfigLabel(null);
    }
  }, [showConfigModal]);

  // Скрываем уведомление о сохранении профиля при переходе на вкладку History
  useEffect(() => {
    if (tab === 'history') {
      setSaveClicked(false);
      if (notification.isVisible && notification.message === 'Profile saved successfully!') {
        setNotification(prev => ({ ...prev, isVisible: false }));
      }
    }
  }, [tab]); 


  const loadProfile = async () => {
    try {
      setIsLoading(true);
      const response = await auth.getProfile();
      if (response.success && response.data) {
        const profile = response.data;
        const sanitizedName = (profile.name || '').slice(0, NAME_MAX_LENGTH);
        const sanitizedCompany = (profile.company_name || '').slice(0, COMPANY_MAX_LENGTH);
        setName(sanitizedName);
        setCompany(sanitizedCompany);
        // Длина, равная лимиту, валидна — не подсвечиваем как ошибку/варнинг.
        setHasNameLimitWarning(false);
        setHasCompanyLimitWarning(false);
        setComm(profile.ready_to_contact || false);
      } else {
        console.warn('Failed to load profile:', response.error);
      }
    } catch (error) {
      console.error('Failed to load profile:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const loadIntegrationsMapForFineTuning = async () => {
    try {
      const resp = await connector.listMyIntegrations();
      const data = resp?.data ?? resp?.items ?? resp ?? [];
      const rows = Array.isArray(data) ? data : [];
      const mapped = rows.reduce((acc: Record<string, string>, row: any) => {
        const id = String(row?.id || row?._id || '').trim();
        if (!id) return acc;
        const labelRaw =
          (typeof row?.label === 'string' && row.label.trim()) ||
          (typeof row?.name === 'string' && row.name.trim()) ||
          (typeof row?.url === 'string' && row.url.trim()) ||
          id;
        acc[id] = labelRaw;
        return acc;
      }, {});
      setIntegrationNameById(mapped);
    } catch {
      setIntegrationNameById({});
    }
  };

  const loadFineTuningHistory = async (opts?: { append?: boolean }) => {
    const append = Boolean(opts?.append);
    try {
      if (!append) {
        setFineTuningEndReached(false);
      }
      if (append) {
        setFineTuningLoadingMore(true);
      } else {
        setFineTuningLoading(true);
      }
      setFineTuningError(null);
      const pageLimit = fineTuningPagination?.limit ?? 100;
      const limit = pageLimit;
      // Rely on loaded row count instead of backend offset echoes, which may be stale.
      const offset = append ? fineTuningHistory.length : 0;
      const resp = await connector.listTrainingHistory({ limit, offset });
      const root = resp as any;
      const payload =
        (root?.data && typeof root.data === 'object' ? root.data : null) ??
        (root?.data?.data && typeof root.data.data === 'object' ? root.data.data : null) ??
        root;
      const rows: any[] = Array.isArray(payload?.items)
        ? payload.items
        : Array.isArray(payload?.data?.items)
          ? payload.data.items
          : Array.isArray(payload)
            ? payload
            : [];
      let normalized = rows.map((r: any) => (r?.data && typeof r.data === 'object' ? r.data : r));

      // Fallback for environments where /training/history may require integration context:
      // collect jobs from all integrations via /training/jobs?integration_id=...
      if (normalized.length === 0 && !append) {
        try {
          const integrationsResp = await connector.listMyIntegrations();
          const integrationsData = integrationsResp?.data ?? integrationsResp?.items ?? integrationsResp ?? [];
          const integrations = Array.isArray(integrationsData) ? integrationsData : [];
          const jobResponses = await Promise.all(
            integrations.map((intg: any) => {
              const integrationId = String(intg?.id || intg?._id || '').trim();
              if (!integrationId) return Promise.resolve(null);
              return connector
                .listTrainingJobs({ integration_id: integrationId, limit: 100, offset: 0 })
                .catch(() => null);
            })
          );
          const merged = jobResponses.flatMap((jr: any) => {
            const items = jr?.data?.items ?? jr?.items ?? [];
            return Array.isArray(items) ? items : [];
          });
          const byId = new Map<string, any>();
          for (const item of merged) {
            const job = item?.data && typeof item.data === 'object' ? item.data : item;
            const id = String(job?.id || '').trim();
            if (!id) continue;
            if (!byId.has(id)) byId.set(id, job);
          }
          normalized = Array.from(byId.values()).sort((a, b) => {
            const ta = Date.parse(String(a?.created_at || '')) || 0;
            const tb = Date.parse(String(b?.created_at || '')) || 0;
            return tb - ta;
          });
        } catch {
          // Keep primary response result if fallback fails.
        }
      }

      const nextPagination =
        root?.pagination ??
        payload?.pagination ??
        payload?.data?.pagination ??
        root?.data?.pagination ??
        null;

      const paginationTotal =
        typeof nextPagination?.total === 'number' ? nextPagination.total : null;
      const reachedByShortPage = normalized.length < limit;
      const reachedByTotal =
        paginationTotal !== null ? offset + normalized.length >= paginationTotal : false;
      const shouldMarkEnd = reachedByShortPage || reachedByTotal;

      if (append) {
        if (shouldMarkEnd) {
          setFineTuningEndReached(true);
        }
        setFineTuningHistory((prev) => {
          const seen = new Set(prev.map((j) => String(j?.id || '').trim()).filter(Boolean));
          const merged: TrainingJob[] = [...prev];
          for (const item of normalized as TrainingJob[]) {
            const id = String(item?.id || '').trim();
            if (id) {
              if (seen.has(id)) continue;
              seen.add(id);
            }
            merged.push(item);
          }
          return merged;
        });
      } else {
        if (shouldMarkEnd) {
          setFineTuningEndReached(true);
        }
        setFineTuningHistory(normalized as TrainingJob[]);
      }

      setFineTuningPagination(nextPagination);
      setFineTuningLoaded(true);
    } catch (e: any) {
      if (append) {
        setFineTuningError(getErrorMessage(e, 'Failed to load more jobs'));
      } else {
        setFineTuningError(getErrorMessage(e, 'Failed to load fine-tuning history'));
        setFineTuningHistory([]);
      }
      setFineTuningLoaded(true);
    } finally {
      setFineTuningLoading(false);
      setFineTuningLoadingMore(false);
    }
  };

  const displayedFineTuningJobs = useMemo(() => {
    let rows = fineTuningHistory;
    const q = fineTuningSearch.trim().toLowerCase();
    if (q) {
      rows = rows.filter((job) => {
        const model = extractFineTuningModelDisplayName(job).toLowerCase();
        const modelId = extractFineTuningConfiguratorModelId(job).toLowerCase();
        const integ = String(job.integration_id || '').toLowerCase();
        const integLabel = (integrationNameById[String(job.integration_id || '')] || integ).toLowerCase();
        const id = String(job.id || '').toLowerCase();
        const status = String(job.status || '').toLowerCase();
        return (
          model.includes(q) ||
          modelId.includes(q) ||
          integ.includes(q) ||
          integLabel.includes(q) ||
          id.includes(q) ||
          status.includes(q)
        );
      });
    }
    if (fineTuningStatusFilter) {
      const sf = fineTuningStatusFilter.toLowerCase();
      rows = rows.filter((job) => String(job.status || '').toLowerCase() === sf);
    }
    if (fineTuningIntegrationFilter) {
      rows = rows.filter((job) => String(job.integration_id || '') === fineTuningIntegrationFilter);
    }
    return rows;
  }, [
    fineTuningHistory,
    fineTuningSearch,
    fineTuningStatusFilter,
    fineTuningIntegrationFilter,
    integrationNameById,
  ]);

  const fineTuningStatusOptions = useMemo(() => {
    const s = new Set<string>();
    for (const j of fineTuningHistory) {
      const st = String(j.status || '').trim();
      if (st) s.add(st);
    }
    return Array.from(s).sort((a, b) => a.localeCompare(b));
  }, [fineTuningHistory]);

  const fineTuningIntegrationOptions = useMemo(() => {
    const fromMap = Object.entries(integrationNameById).map(([id, label]) => ({ id, label: label || id }));
    const idsFromJobs = new Set(
      fineTuningHistory.map((j) => String(j.integration_id || '').trim()).filter(Boolean)
    );
    const extra = Array.from(idsFromJobs)
      .filter((id) => !integrationNameById[id])
      .map((id) => ({ id, label: id.length > 10 ? `${id.slice(0, 8)}…` : id }));
    return [...fromMap, ...extra].sort((a, b) => a.label.localeCompare(b.label));
  }, [integrationNameById, fineTuningHistory]);

  const fineTuningHasMore = useMemo(() => {
    if (fineTuningEndReached) return false;
    const total = fineTuningPagination?.total;
    if (typeof total === 'number' && total > 0) {
      return fineTuningHistory.length < total;
    }
    return false;
  }, [fineTuningEndReached, fineTuningPagination, fineTuningHistory.length]);

  const fineTuningFiltersActive = Boolean(
    fineTuningSearch.trim() || fineTuningStatusFilter || fineTuningIntegrationFilter
  );

  // Filtering is performed client-side. Finish loading the source collection so
  // filtered results are complete and users never have to click through pages
  // that contain no matching jobs.
  useEffect(() => {
    if (
      tab !== 'fine-tuning' ||
      !fineTuningFiltersActive ||
      !fineTuningHasMore ||
      fineTuningLoading ||
      fineTuningLoadingMore
    ) {
      return;
    }
    void loadFineTuningHistory({ append: true });
    // loadFineTuningHistory intentionally uses the latest render state. The
    // scalar dependencies below drive one request per appended page.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    tab,
    fineTuningFiltersActive,
    fineTuningHasMore,
    fineTuningHistory.length,
    fineTuningLoading,
    fineTuningLoadingMore,
  ]);

  const handleLoadFineTuningToConnector = (job: TrainingJob) => {
    const integrationId = String(job?.integration_id || '').trim();
    const modelId = extractFineTuningConfiguratorModelId(job);
    const displayName = extractFineTuningModelDisplayName(job);
    if (!integrationId) {
      setNotification({ isVisible: true, type: 'error', message: 'Integration ID is missing for this fine-tuning job.' });
      return;
    }
    if (!modelId) {
      setNotification({ isVisible: true, type: 'error', message: 'Fine-tuned model is not available for this job yet.' });
      return;
    }
    toggleConfiguratorModel(integrationId, modelId, true);
    setNotification({
      isVisible: true,
      type: 'success',
      message: `Model ${displayName || modelId} added to Connector for this integration.`,
    });
    const jobId = String(job?.id || '').trim();
    navigate(
      `/connector?integrationId=${encodeURIComponent(integrationId)}${jobId ? `&ftJobId=${encodeURIComponent(jobId)}` : ''}`
    );
  };

  const openFineTuningJobDetails = (job: TrainingJob) => {
    setFineTuningRetryError(null);
    setSelectedFineTuningJob(job);
  };

  useEffect(() => {
    if (!selectedFineTuningJob) return;

    const jobId = resolveTrainingJobRecordId(selectedFineTuningJob);
    const integrationId = String(selectedFineTuningJob.integration_id || '').trim();
    const status = resolveEffectiveTrainingJobStatus(selectedFineTuningJob);
    if (!jobId || !integrationId || !isActiveJobStatus(status)) return;

    let cancelled = false;

    const pollLiveJob = async () => {
      try {
        const response = await connector.pollTrainingJob(jobId, integrationId);
        if (cancelled) return;
        const enriched = enrichTrainingJobRowForDisplay(response?.data ?? response);
        setSelectedFineTuningJob((prev) => {
          if (!prev || resolveTrainingJobRecordId(prev) !== jobId) return prev;
          return enriched as unknown as TrainingJob;
        });
      } catch {
        // Keep last known snapshot in the modal if a poll fails.
      }
    };

    void pollLiveJob();
    const intervalId = window.setInterval(() => {
      void pollLiveJob();
    }, 5000);

    return () => {
      cancelled = true;
      window.clearInterval(intervalId);
    };
  }, [selectedFineTuningJob?.id, selectedFineTuningJob?.integration_id, selectedFineTuningJob?.status]);

  const handleRetryFineTuningJob = async (job: TrainingJob, rowKey: string) => {
    const integrationId = String(job.integration_id || '').trim();
    const jobId = resolveTrainingJobRecordId(job);
    if (!integrationId || !jobId || !rowKey || fineTuningRetryingRowKey) return;

    setFineTuningRetryError(null);
    setFineTuningRetryingRowKey(rowKey);
    try {
      const response = await connector.retryTrainingJob(jobId, integrationId);
      const newJobId = assertTrainingJobRetrySucceeded(response);
      setSelectedFineTuningJob(null);
      setFineTuningRetryError(null);
      setNotification({
        isVisible: true,
        type: 'success',
        message: 'Fine-tuning job restarted. Opening the new job in Connector.',
      });
      setTimeout(() => setNotification((prev) => ({ ...prev, isVisible: false })), NOTIFICATION_AUTO_HIDE_MS);
      navigate(
        `/connector?integrationId=${encodeURIComponent(integrationId)}&ftJobId=${encodeURIComponent(newJobId)}`
      );
    } catch (e: unknown) {
      const message = getErrorMessage(e, 'Failed to retry fine-tuning job');
      setFineTuningRetryError(message);
      setNotification({
        isVisible: true,
        type: 'error',
        message,
      });
      setTimeout(() => setNotification((prev) => ({ ...prev, isVisible: false })), NOTIFICATION_AUTO_HIDE_MS);
    } finally {
      setFineTuningRetryingRowKey(null);
    }
  };

  const loadHistory = async (opts?: { append?: boolean }) => {
    const append = Boolean(opts?.append);
    try {
      if (append) {
        setHistoryLoadingMore(true);
      } else {
        setIsHistoryLoading(true);
      }

      const offset = append ? allHistoryItems.length : 0;
      const response = await autoTune.getOptimizationHistory({
        limit: OPTIMIZATION_HISTORY_PAGE_SIZE,
        offset,
      });

      if (response?.success) {
        const { items, pagination } = parseOptimizationHistoryResponse(response);
        const merged = append ? [...allHistoryItems, ...items] : items;
        const { multiAgent, integrations } = splitOptimizationHistoryByCategory(merged);

        setAllHistoryItems(merged);
        setMultiAgentHistory(multiAgent);
        setIntegrationsHistory(integrations);
        setHistory(historyCategory === 'multi-agent' ? multiAgent : integrations);
        setHistoryPagination(pagination);
        setHistoryLoaded(true);
      } else {
        if (!append) {
          setAllHistoryItems([]);
          setMultiAgentHistory([]);
          setIntegrationsHistory([]);
          setHistory([]);
          setHistoryPagination(null);
        }
        setHistoryLoaded(true);

        if (response?.error) {
          setNotification({
            isVisible: true,
            type: 'error',
            message: capitalizeError(response.error.message) || 'Failed to load history',
          });
        }
      }
    } catch (error: unknown) {
      if (!append) {
        setAllHistoryItems([]);
        setMultiAgentHistory([]);
        setIntegrationsHistory([]);
        setHistory([]);
        setHistoryPagination(null);
        setHistoryLoaded(true);
      }

      const err = error as { message?: string; response?: { data?: { error?: { message?: string } } } };
      const errorMessage = err?.message?.includes('timeout')
        ? 'History loading timed out. Please try again.'
        : capitalizeError(err?.response?.data?.error?.message || err?.message) ||
          'Failed to load history. Please try again.';

      setNotification({
        isVisible: true,
        type: 'error',
        message: errorMessage,
      });
    } finally {
      setIsHistoryLoading(false);
      setHistoryLoadingMore(false);
    }
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();

    const trimmedName = name.trim();
    if (trimmedName.length === 0) {
      setNameRequiredError(true);
      // Это ожидаемая валидация формы — не показываем общий "Failed to save profile" и не шлем запрос
      setNotification(prev => ({ ...prev, isVisible: false }));
      nameInputRef.current?.focus();
      return;
    }

    if (name.length > NAME_MAX_LENGTH || company.length > COMPANY_MAX_LENGTH) {
      // Если вдруг в состояние попало значение длиннее лимита — подсветим и не шлём запрос
      setHasNameLimitWarning(name.length > NAME_MAX_LENGTH);
      setHasCompanyLimitWarning(company.length > COMPANY_MAX_LENGTH);
      return;
    }
    try {
      setIsLoading(true);
      setNotification(prev => ({ ...prev, isVisible: false }));
      
      const response = await auth.updateProfile({
        name: trimmedName,
        company_name: company,
        ready_to_contact: comm
      });
      
      if (response.success) {
        setNotification({
          isVisible: true,
          type: 'info',
          message: 'Profile saved successfully!'
        });
      } else {
        setNotification({
          isVisible: true,
          type: 'error',
          message: capitalizeError(response.error?.message) || 'Failed to save profile'
        });
      }
    } catch (error: any) {
      console.error('Failed to save profile:', error);
      const status = error?.response?.status;

      // На 400 стараемся показать пользовательскую (полевую) валидацию вместо общего тоста.
      if (status === 400) {
        const currentTrimmedName = name.trim();
        if (currentTrimmedName.length === 0) {
          setNameRequiredError(true);
          nameInputRef.current?.focus();
          return;
        }
      }

      setNotification({
        isVisible: true,
        type: 'error',
        message: getErrorMessage(error, 'Failed to save profile. Please try again.')
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleNameChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    if (value.length > NAME_MAX_LENGTH) {
      // Мягко обрезаем до лимита и показываем хинт (не "ошибочный" вид поля).
      setName(value.slice(0, NAME_MAX_LENGTH));
      setHasNameLimitWarning(true);
    } else {
      setName(value);
      setHasNameLimitWarning(false);
    }
    if (value.trim().length > 0) setNameRequiredError(false);
  };

  const handleCompanyChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    if (value.length > COMPANY_MAX_LENGTH) {
      setCompany(value.slice(0, COMPANY_MAX_LENGTH));
      setHasCompanyLimitWarning(true);
    } else {
      setCompany(value);
      setHasCompanyLimitWarning(false);
    }
  };

  const formatDate = (dateString: string) => {
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) {
        return 'Invalid date';
      }
      return date.toLocaleDateString('ru-RU', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (error) {
      return 'Invalid date';
    }
  };

  // Форматирование даты в формат "MMM DD, YYYY" (например, Jan 15, 2024)
  const formatDateShort = (dateString: string) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) {
        return 'N/A';
      }
      const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
      const month = monthNames[date.getMonth()];
      const day = date.getDate();
      const year = date.getFullYear();
      return `${month} ${day.toString().padStart(2, '0')}, ${year}`;
    } catch {
      return 'N/A';
    }
  };

  // Форматирование score
  const formatScore = (score: number | undefined | null): string => {
    if (score === undefined || score === null || isNaN(score)) {
      return 'N/A';
    }
    return score.toFixed(4);
  };

  const handleViewHistory = (item: OptimizationHistoryItem) => {
    console.log('🔍 handleViewHistory called with item:', item);
    
    // Получаем integration_id из item
    const integrationId = item.integration_id;
    
    // Поддерживаем оба формата: старый (master-agent/worker-agents) и новый (coordinator/workers)
    const config = item.configuration || {};
    
    // Проверяем старый формат multi-agent
    const masterAgent = extractCoordinatorFromConfig(config);
    const workerAgents = extractWorkersFromConfig(config);
    
    // Проверяем новый формат connector/integration
    const coordinator = (config as any)?.coordinator;
    const workers = (config as any)?.workers || [];
    
    // Также проверяем в step.data для интеграций
    const step0 = item.steps?.find(step => step.step_number === 0);
    const step0Coordinator = extractCoordinatorFromData(step0?.data);
    const step0Workers = extractWorkersFromData(step0?.data);
    
    // Определяем, какой формат использовать
    let finalCoordinator: any = null;
    let finalWorkers: any[] = [];
    
    if (coordinator) {
      // Новый формат connector/integration
      finalCoordinator = coordinator;
      finalWorkers = workers;
    } else if (masterAgent) {
      // Старый формат multi-agent
      finalCoordinator = masterAgent;
      finalWorkers = workerAgents;
    } else if (step0Coordinator) {
      // Данные в step.data
      finalCoordinator = step0Coordinator;
      finalWorkers = step0Workers;
    }
    
    if (!finalCoordinator) {
      console.error(OPTIMIZATION_COORDINATOR_MISSING_MESSAGE, {
        config,
        step0: step0?.data,
        hasMasterAgent: !!masterAgent,
        hasCoordinator: !!coordinator,
        hasStep0Coordinator: !!step0Coordinator
      });
      setNotification({
        isVisible: true,
        type: 'error',
        message: OPTIMIZATION_COORDINATOR_MISSING_MESSAGE
      });
      return;
    }
    
    // Загружаем конфигурацию в store для multi-agent
    const store = useOptimizationStore.getState();
    
    // Используем правильные поля в зависимости от формата
    const coordinatorPrompt = finalCoordinator.prompt || '';
    const coordinatorModel = finalCoordinator.model_name || finalCoordinator.model || '';
    const coordinatorTemperature = finalCoordinator.temperature || 0;
    const coordinatorName = finalCoordinator.name || 'master';
    const coordinatorDescription = finalCoordinator.description || '';
    
    if (!coordinatorPrompt) {
      setNotification({
        isVisible: true,
        type: 'error',
        message: 'Invalid multi-agent configuration: coordinator prompt is missing.'
      });
      return;
    }
    
    if (!coordinatorModel) {
      setNotification({
        isVisible: true,
        type: 'error',
        message: 'Invalid multi-agent configuration: coordinator model is missing.'
      });
      return;
    }
    
    // Преобразуем workers в формат store
    const storeWorkers = finalWorkers.map((agent, index) => ({
      id: agent.id || index + 1,
      name: agent.name || `Worker ${index + 1}`,
      prompt: agent.prompt || '',
      model: agent.model_name || agent.model || '',
      temperature: agent.temperature || 0,
      description: agent.description || '',
      mode: agent.mode || 'asynchronous' as 'synchronous' | 'asynchronous',
      use_rag: agent.use_rag || false,
      use_ltm: agent.use_ltm || false,
      reminder_prompt: typeof agent.reminder_prompt === 'string' ? agent.reminder_prompt : undefined,
    }));
    
    // Валидация workers
    const invalidWorkers = storeWorkers.filter(w => !w.prompt || !w.model);
    if (invalidWorkers.length > 0) {
      setNotification({
        isVisible: true,
        type: 'error',
        message: `Invalid multi-agent configuration: ${invalidWorkers.length} worker(s) missing prompt or model.`
      });
      return;
    }
    
    // Определяем, куда редиректить в зависимости от активной вкладки
    if (historyCategory === 'integrations') {
      // Для integrations используем connector store методы
      store.setConnectorCoordinatorPrompt(coordinatorPrompt);
      store.setConnectorCoordinatorModel(coordinatorModel);
      store.setConnectorCoordinatorTemperature(coordinatorTemperature);
      if (coordinatorDescription) {
        store.setConnectorCoordinatorDescription(coordinatorDescription);
      }
      store.setConnectorCoordinatorReminderPrompt(
        typeof finalCoordinator?.reminder_prompt === 'string' ? finalCoordinator.reminder_prompt : ''
      );
      store.setConnectorWorkers(storeWorkers);
      store.setSelectedDatasets(normalizeOptimizationDatasetNames(config.dataset_name));
      store.setConnectorConfigLabel((item.config_label ?? '').trim() || undefined);

      // Обрабатываем steps для connector
      if (item.steps && item.steps.length > 0) {
        const iterationResults = item.steps
          .filter(step => step.step_number > 0) // Только оптимизированные шаги
          .map((step) => {
            // Извлекаем данные агентов из step.data, если они есть
            const stepMasterAgent = extractCoordinatorFromData(step.data) || finalCoordinator;
            const stepWorkerAgents = extractWorkersFromData(step.data).length > 0 ? extractWorkersFromData(step.data) : finalWorkers;
            
            return {
              prompt: step.prompt || stepMasterAgent?.prompt || coordinatorPrompt,
              iteration: step.step_number,
              seq: step.step_number,
              temperature: step.temperature || stepMasterAgent?.temperature || coordinatorTemperature,
              embedding_point: step.embedding_point,
              evaluation_point: step.evaluation_point,
              "master-agent": stepMasterAgent,
              "worker-agents": stepWorkerAgents
            };
          });
        
        store.setConnectorMultiAgentIterationResults(iterationResults);
        store.setConnectorMultiAgentIsOptimized(iterationResults.length > 0);
        store.setConnectorMultiAgentSelectedIterationIndex(0);
      } else {
        store.setConnectorMultiAgentIterationResults([]);
        store.setConnectorMultiAgentIsOptimized(false);
      }
      
      const metaFromConfig = (config as any)?.metaPrompts || (config as any)?.meta_prompts;
      const metaPromptsData = metaFromConfig || extractMetaPromptsFromData(step0?.data);
      const step0Payload = step0?.data as Record<string, unknown> | undefined;
      const rp = step0Payload?.reminder_prompt_position ?? step0Payload?.reminderPromptPosition;
      stashConnectorPendingConfig(
        buildAgentConfigFromData(finalCoordinator, finalWorkers, metaPromptsData, rp)
      );

      // Редиректим на connector с integration_id в URL, если он есть
      if (integrationId) {
        navigate(`/connector?integrationId=${integrationId}`);
      } else {
        navigate('/connector');
      }
    } else {
      // Для multi-agent используем multi-agent store методы
      store.setCoordinatorPrompt(coordinatorPrompt);
      store.setCoordinatorModel(coordinatorModel);
      store.setCoordinatorTemperature(coordinatorTemperature);
      if (coordinatorDescription) {
        store.setCoordinatorDescription(coordinatorDescription);
      }
      store.setSelectedDatasets(normalizeOptimizationDatasetNames(config.dataset_name));
      store.setMultiAgentIterations(config.iterations || 4);
      store.setWorkers(storeWorkers);
      
      // Обрабатываем steps для multi-agent
      if (item.steps && item.steps.length > 0) {
        const iterationResults = item.steps
          .filter(step => step.step_number > 0) // Только оптимизированные шаги
          .map((step) => {
            // Извлекаем данные агентов из step.data, если они есть
            const stepMasterAgent = extractCoordinatorFromData(step.data) || finalCoordinator;
            const stepWorkerAgents = extractWorkersFromData(step.data).length > 0 ? extractWorkersFromData(step.data) : finalWorkers;
            
            return {
              prompt: step.prompt || stepMasterAgent?.prompt || coordinatorPrompt,
              iteration: step.step_number,
              seq: step.step_number,
              temperature: step.temperature || stepMasterAgent?.temperature || coordinatorTemperature,
              embedding_point: step.embedding_point,
              evaluation_point: step.evaluation_point,
              "master-agent": stepMasterAgent,
              "worker-agents": stepWorkerAgents
            };
          });
        
        store.setMultiAgentIterationResults(iterationResults);
        store.setMultiAgentIsOptimized(iterationResults.length > 0);
        store.setMultiAgentSelectedIterationIndex(0);
      } else {
        store.setMultiAgentIterationResults([]);
        store.setMultiAgentIsOptimized(false);
      }
      
      // Редиректим на multi-agent
      navigate('/multi-agent');
    }
  };

  const resolveHistoryRunConfigLabel = (
    runConfigLabel?: string | null,
    configuration?: OptimizationHistoryItem['configuration'] | null
  ): string | null => {
    const fromRun = typeof runConfigLabel === 'string' ? runConfigLabel.trim() : '';
    if (fromRun) return fromRun;
    const cfg = configuration as any;
    const fromCfg =
      (typeof cfg?.config_label === 'string' && cfg.config_label.trim()) ||
      (typeof cfg?.configLabel === 'string' && cfg.configLabel.trim()) ||
      (typeof cfg?.label === 'string' && cfg.label.trim()) ||
      '';
    return fromCfg || null;
  };

  const handleViewSteps = (
    steps: OptimizationHistoryItem['steps'],
    selectedStepId?: number,
    configuration?: OptimizationHistoryItem['configuration'],
    runId?: string,
    integrationId?: string,
    runConfigLabel?: string | null
  ) => {
    setSelectedHistoryRunConfigLabel(resolveHistoryRunConfigLabel(runConfigLabel, configuration ?? null));

    // Проверяем, что steps существует и является массивом
    if (!steps || !Array.isArray(steps)) {
      console.warn('⚠️ handleViewSteps: steps is null or not an array', steps);
      // Для multi-agent создаем пустой массив или массив с step 0 из конфигурации
      const defaultSteps: OptimizationStep[] = [];
      const configurationCoordinator = extractCoordinatorFromConfig(configuration);
      if (configurationCoordinator) {
        defaultSteps.push({
          step_number: 0,
          prompt: configurationCoordinator.prompt || '',
          model_name: configurationCoordinator.model_name || configurationCoordinator.model || '',
          temperature: configurationCoordinator.temperature || 0,
          timestamp: new Date().toISOString()
        });
      } else if (configuration?.initial_prompt) {
        defaultSteps.push({
          step_number: 0,
          prompt: configuration.initial_prompt,
          model_name: configuration.model_name || '',
          temperature: configuration.temperature || 0,
          timestamp: new Date().toISOString()
        });
      }
      const normalizedSteps = normalizeOptimizationSteps(defaultSteps);
      setSelectedSteps(normalizedSteps);
      setSelectedStepId(selectedStepId || null);
      // Раскрываем все шаги по умолчанию
      setExpandedSteps(new Set(normalizedSteps.map(s => s.step_number)));
      setExpandedAgents(new Set());
      setShowStepsModal(true);
      return;
    }
    
    // Обрабатываем steps: заполняем пустые шаги данными из data или конфигурации
    const processedSteps: OptimizationStep[] = steps.map((step) => {
      const masterAgent = extractCoordinatorFromConfig(configuration);
      const configWorkers = extractWorkersFromConfig(configuration);
      
      // Извлекаем данные из step.data если основные поля пустые
      const stepDataMaster = extractCoordinatorFromData(step.data);
      const stepDataWorkers = extractWorkersFromData(step.data);
      const normalizedStepData = {
        ...(unwrapStepData(step.data) || {}),
        ...(stepDataMaster ? {} : (masterAgent ? { "master-agent": masterAgent } : {})),
        ...(stepDataWorkers.length > 0 ? {} : (configWorkers.length > 0 ? { "worker-agents": configWorkers } : {})),
      };
      const hasStepData = stepDataMaster && (stepDataMaster.prompt || stepDataMaster.model_name);
      
      // Определяем, пустой ли шаг (нет prompt, model_name, temperature)
      const isEmpty = !step.prompt && !step.model_name && step.temperature === undefined;
      
      // Для step 0: приоритет - step.data > configuration > пустые значения
      if (step.step_number === 0) {
        // Если есть данные в step.data, используем их
        if (hasStepData) {
          return {
            ...step,
            prompt: step.prompt || stepDataMaster.prompt || '',
            model_name: step.model_name || stepDataMaster.model_name || '',
            temperature: step.temperature !== undefined ? step.temperature : (stepDataMaster.temperature ?? 0),
            timestamp: step.timestamp || new Date().toISOString(),
            data: normalizedStepData as any,
          };
        }
        
        // Если шаг пустой и есть master-agent в конфигурации
        if (isEmpty && masterAgent) {
          return {
            ...step,
            prompt: masterAgent.prompt || '',
            model_name: masterAgent.model_name || '',
            temperature: masterAgent.temperature ?? 0,
            timestamp: step.timestamp || new Date().toISOString(),
            data: normalizedStepData as any,
          };
        }
        
        // Если шаг пустой и есть initial_prompt (single-agent)
        if (isEmpty && configuration?.initial_prompt) {
          return {
            ...step,
            prompt: configuration.initial_prompt,
            model_name: configuration.model_name || '',
            temperature: configuration.temperature ?? 0,
            timestamp: step.timestamp || new Date().toISOString(),
            data: normalizedStepData as any,
          };
        }
        
        // Если шаг не пустой, но некоторые поля отсутствуют, заполняем из конфигурации
        return {
          ...step,
          prompt: step.prompt || (masterAgent?.prompt) || (configuration?.initial_prompt) || '',
          model_name: step.model_name || (masterAgent?.model_name) || (configuration?.model_name) || '',
          temperature: step.temperature !== undefined ? step.temperature : (masterAgent?.temperature ?? configuration?.temperature ?? 0),
          timestamp: step.timestamp || new Date().toISOString(),
          data: normalizedStepData as any,
        };
      }
      
      // Для остальных шагов (step_number > 0): извлекаем из step.data если пусто
      if (isEmpty && hasStepData) {
        return {
          ...step,
          prompt: stepDataMaster.prompt || '',
          model_name: stepDataMaster.model_name || '',
          temperature: stepDataMaster.temperature ?? 0,
          timestamp: step.timestamp || new Date().toISOString(),
          data: normalizedStepData as any,
        };
      }
      
      // Возвращаем шаг с заполненными значениями по умолчанию
      return {
        ...step,
        prompt: step.prompt || stepDataMaster?.prompt || masterAgent?.prompt || '',
        model_name: step.model_name || stepDataMaster?.model_name || stepDataMaster?.model || masterAgent?.model_name || masterAgent?.model || '',
        temperature: step.temperature ?? stepDataMaster?.temperature ?? masterAgent?.temperature ?? 0,
        timestamp: step.timestamp || new Date().toISOString(),
        data: normalizedStepData as any,
      };
    });
    
    const normalizedSteps = normalizeOptimizationSteps(processedSteps);
    setSelectedSteps(normalizedSteps);
    setSelectedStepId(selectedStepId || null);
    setSelectedRunId(runId || null);
    setSelectedIntegrationId(integrationId || null);
    // Раскрываем все шаги по умолчанию
    setExpandedSteps(new Set(normalizedSteps.map(s => s.step_number)));
    setExpandedAgents(new Set());
    setShowStepsModal(true);
  };
  
  const handleExportStep = async (stepNumber: number, runId?: string, stepDataOverride?: OptimizationStep, configOverride?: OptimizationConfiguration) => {
    const targetRunId = runId || selectedRunId;
    if (!targetRunId) {
      setNotification({
        isVisible: true,
        type: 'error',
        message: 'Run ID is missing. Cannot export step.'
      });
      return;
    }
    
    try {
      setIsLoading(true);
      
      // Используем переданные данные или ищем в selectedSteps
      let stepData = stepDataOverride;
      if (!stepData) {
        stepData = selectedSteps.find(step => step.step_number === stepNumber);
      }
      
      if (!stepData) {
        setNotification({
          isVisible: true,
          type: 'error',
          message: `Step ${stepNumber} not found in the optimization history.`
        });
        setIsLoading(false);
        return;
      }
      
      // Используем переданную конфигурацию или selectedConfig
      const targetConfig = configOverride || selectedConfig;
      
      // Создаем объект для экспорта
      const exportData = {
        run_id: targetRunId,
        step_number: stepNumber,
        step: {
          step_number: stepData.step_number,
          prompt: stepData.prompt || '',
          model_name: stepData.model_name || '',
          temperature: stepData.temperature || 0,
          timestamp: stepData.timestamp || new Date().toISOString(),
          embedding_point: stepData.embedding_point,
          evaluation_point: stepData.evaluation_point
        },
        configuration: targetConfig || null,
        exported_at: new Date().toISOString()
      };
      
      // Создаем JSON файл
      const jsonString = JSON.stringify(exportData, null, 2);
      const blob = new Blob([jsonString], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `optimization-step-${stepNumber}-run-${targetRunId}-${Date.now()}.json`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      
      // Также отправляем данные на сервер для сохранения в интеграцию (если нужно)
      try {
        const response = await autoTune.exportStep({
          run_id: targetRunId,
          step_number: stepNumber
        });
        
        // Структура ответа: response.data.data.data содержит нужные данные
        const responseData = (response as any)?.data;
        const innerData = responseData?.data;
        const integrationData = innerData?.data;
        
        // Парсим config из JSON строки, если он есть
        let label = '';
        if (integrationData?.config) {
          try {
            const config = JSON.parse(integrationData.config);
            label = config.label || integrationData.label || '';
          } catch (e) {
            label = integrationData.label || '';
          }
        } else if (integrationData?.label) {
          label = integrationData.label;
        }
        
        setNotification({
          isVisible: true,
          type: 'success',
          message: `Step ${stepNumber} exported successfully${label ? ` to integration (${label})` : ''}`
        });
      } catch (serverError: any) {
        // Если серверная часть не работает, все равно показываем успех для скачивания файла
        console.warn('Server export failed, but file download succeeded:', serverError);
        setNotification({
          isVisible: true,
          type: 'success',
          message: `Step ${stepNumber} exported successfully (file downloaded)`
        });
      }
    } catch (error: any) {
      let errorMessage = 'Failed to export step';
      
      // Обработка различных типов ошибок
      if (error?.response?.status === 400) {
        const message = error?.response?.data?.error?.message || error?.response?.data?.message;
        if (message?.includes('integration_id') || message?.includes('integration')) {
          errorMessage = 'This optimization is not linked to an integration. Cannot export step.';
        } else if (message?.includes('step_number') || message?.includes('step')) {
          errorMessage = 'Invalid step number. Please select a valid step.';
        } else {
          errorMessage = message || 'Invalid request. Please check the step number and run ID.';
        }
      } else if (error?.response?.status === 403) {
        errorMessage = 'You do not have permission to export this step.';
      } else if (error?.response?.status === 404) {
        errorMessage = 'Optimization run or step not found. It may have been deleted.';
      } else if (error?.response?.status === 500) {
        const message = error?.response?.data?.error?.message || error?.response?.data?.message || '';
        if (message.includes('connector') || message.includes('404') || message.includes('Not found')) {
          errorMessage = 'Integration not found in connector. The integration may have been deleted or is unavailable.';
        } else {
          errorMessage = 'Server error occurred while exporting step. Please try again later.';
        }
      } else if (error?.response?.data?.error?.message) {
        errorMessage = error.response.data.error.message;
      } else if (error?.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error?.message) {
        errorMessage = error.message;
      }
      
      setNotification({
        isVisible: true,
        type: 'error',
        message: capitalizeError(errorMessage)
      });
    } finally {
      setIsLoading(false);
    }
  };

  const toggleStep = (stepNumber: number) => {
    setExpandedSteps(prev => {
      const newSet = new Set(prev);
      if (newSet.has(stepNumber)) {
        newSet.delete(stepNumber);
      } else {
        newSet.add(stepNumber);
      }
      return newSet;
    });
  };

  const toggleAgent = (agentKey: string) => {
    setExpandedAgents(prev => {
      const newSet = new Set(prev);
      if (newSet.has(agentKey)) {
        newSet.delete(agentKey);
      } else {
        newSet.add(agentKey);
      }
      return newSet;
    });
  };

  const buildAgentConfigFromData = (
    coordinatorData: any,
    workersData: any[],
    metaPromptsData?: any,
    reminderPromptPositionRaw?: unknown
  ): import('../types/agentConfigurator').AgentConfiguratorState => {
    return {
      coordinator: {
        name: coordinatorData.name || 'AgentCoordinator',
        description: coordinatorData.description || '',
        prompt: coordinatorData.prompt || '',
        model: coordinatorData.model || coordinatorData.model_name || 'gpt-4.1-mini',
        temperature: coordinatorData.temperature ?? 0.8,
        use_rag: coordinatorData.use_rag || false,
        use_ltm: coordinatorData.use_ltm || false,
        reminder_prompt:
          typeof coordinatorData.reminder_prompt === 'string'
            ? coordinatorData.reminder_prompt
            : undefined,
        ...(typeof coordinatorData.id === 'string' && coordinatorData.id.trim()
          ? { id: coordinatorData.id.trim() }
          : {}),
        ...(typeof coordinatorData.agent_id === 'string' && coordinatorData.agent_id.trim()
          ? { agent_id: coordinatorData.agent_id.trim() }
          : {}),
        rag_doc_ids: normalizeLegacyRagDocIds(coordinatorData.rag_doc_ids),
      },
      workers: workersData.map((w, i) => ({
        id: normalizeWorkerIdString(w.id, i),
        name: w.name || w.agent_name || w.worker_name || `Worker ${i + 1}`,
        description: w.description || '',
        prompt: w.prompt || '',
        mode: (w.mode === 'asynchronous' ? 'asynchronous' : 'synchronous') as 'synchronous' | 'asynchronous',
        model: w.model || w.model_name || 'gpt-4.1-mini',
        temperature: w.temperature ?? 0.7,
        use_rag: w.use_rag || false,
        use_ltm: w.use_ltm || false,
        is_trainable: typeof w.is_trainable === 'boolean' ? w.is_trainable : true,
        reminder_prompt: typeof w.reminder_prompt === 'string' ? w.reminder_prompt : undefined,
        rag_doc_ids: normalizeLegacyRagDocIds(w.rag_doc_ids),
      })),
      metaPrompts: mergeMetaPromptsWithFinalStepDefault(
        metaPromptsData && typeof metaPromptsData === 'object'
          ? (Object.fromEntries(
              Object.entries(metaPromptsData).filter(([, value]) => typeof value === 'string')
            ) as Record<string, string>)
          : null
      ),
      reminder_prompt_position: normalizeReminderPromptPosition(reminderPromptPositionRaw),
    };
  };

  /** Load entire integration run: initial config → Connector initial UI; all steps with step_number > 0 → optimized carousel. */
  const handleLoadFullOptimizationToConnector = (item: OptimizationHistoryItem) => {
    const config = item.configuration || {};
    const integrationId = item.integration_id;

    const masterAgent = extractCoordinatorFromConfig(config);
    const workerAgents = extractWorkersFromConfig(config);
    const coordinator = (config as any)?.coordinator;
    const workers = (config as any)?.workers || [];

    const step0 = item.steps?.find((s) => s.step_number === 0);
    const step0Coordinator = extractCoordinatorFromData(step0?.data);
    const step0Workers = extractWorkersFromData(step0?.data);

    let finalCoordinator: any = null;
    let finalWorkers: any[] = [];

    if (coordinator) {
      finalCoordinator = coordinator;
      finalWorkers = workers;
    } else if (masterAgent) {
      finalCoordinator = masterAgent;
      finalWorkers = workerAgents;
    } else if (step0Coordinator) {
      finalCoordinator = step0Coordinator;
      finalWorkers = step0Workers;
    }

    if (!finalCoordinator) {
      setNotification({ isVisible: true, type: 'error', message: OPTIMIZATION_COORDINATOR_MISSING_MESSAGE });
      return;
    }

    const metaFromConfig = (config as any)?.metaPrompts || (config as any)?.meta_prompts;
    const metaPromptsData = metaFromConfig || extractMetaPromptsFromData(step0?.data);
    const step0Payload = step0?.data as Record<string, unknown> | undefined;
    const rp = step0Payload?.reminder_prompt_position ?? step0Payload?.reminderPromptPosition;

    const workersForCfg = Array.isArray(finalWorkers) ? finalWorkers : [];
    const cfg = buildAgentConfigFromData(finalCoordinator, workersForCfg, metaPromptsData, rp);

    if (!String(cfg.coordinator.prompt || '').trim()) {
      setNotification({
        isVisible: true,
        type: 'error',
        message: 'Invalid configuration: coordinator prompt is missing.',
      });
      return;
    }

    const store = useOptimizationStore.getState();

    store.setConnectorCoordinatorPrompt(cfg.coordinator.prompt);
    store.setConnectorCoordinatorModel(cfg.coordinator.model);
    store.setConnectorCoordinatorTemperature(cfg.coordinator.temperature);
    store.setConnectorCoordinatorDescription(cfg.coordinator.description);
    store.setConnectorCoordinatorReminderPrompt(cfg.coordinator.reminder_prompt || '');
    store.setConnectorCoordinatorName(
      typeof cfg.coordinator.name === 'string' && cfg.coordinator.name.trim()
        ? cfg.coordinator.name.trim()
        : 'AgentCoordinator'
    );
    store.setConnectorCoordinatorBackendId(
      typeof cfg.coordinator.id === 'string' ? cfg.coordinator.id.trim() || undefined : undefined
    );
    store.setConnectorCoordinatorAgentId(
      typeof cfg.coordinator.agent_id === 'string'
        ? cfg.coordinator.agent_id.trim() || undefined
        : typeof cfg.coordinator.id === 'string'
          ? cfg.coordinator.id.trim() || undefined
          : undefined
    );
    store.setConnectorReminderPromptPosition(cfg.reminder_prompt_position ?? 'before');
    store.setConnectorConfigLabel(
      (item.config_label ?? '').trim() ||
        (config as any)?.label ||
        (config as any)?.config_label ||
        (config as any)?.configLabel ||
        undefined
    );
    store.setConnectorWorkers(
      cfg.workers.map((w, i) => ({
        id: normalizeWorkerIdString(w.id, i),
        name: w.name,
        prompt: w.prompt,
        model: w.model,
        temperature: w.temperature,
        description: w.description,
        mode: w.mode ?? 'synchronous',
        reminder_prompt: w.reminder_prompt,
      }))
    );
    store.setConnectorMetaPrompts(mergeMetaPromptsWithFinalStepDefault(cfg.metaPrompts));
    store.setSelectedDatasets(normalizeOptimizationDatasetNames((config as any)?.dataset_name));

    const coordinatorPrompt = finalCoordinator.prompt || '';
    const coordinatorTemperature =
      typeof finalCoordinator.temperature === 'number' && Number.isFinite(finalCoordinator.temperature)
        ? finalCoordinator.temperature
        : cfg.coordinator.temperature;

    if (item.steps && item.steps.length > 0) {
      const iterationResults = item.steps
        .filter((step) => step.step_number > 0)
        .map((step) => {
          const stepMasterAgent = extractCoordinatorFromData(step.data) || finalCoordinator;
          const stepWorkerAgents =
            extractWorkersFromData(step.data).length > 0 ? extractWorkersFromData(step.data) : finalWorkers;

          return {
            prompt: step.prompt || stepMasterAgent?.prompt || coordinatorPrompt,
            iteration: step.step_number,
            seq: step.step_number,
            temperature:
              typeof step.temperature === 'number' && Number.isFinite(step.temperature)
                ? step.temperature
                : stepMasterAgent?.temperature ?? coordinatorTemperature,
            embedding_point: step.embedding_point,
            evaluation_point: step.evaluation_point,
            'master-agent': stepMasterAgent,
            'worker-agents': stepWorkerAgents,
          };
        });

      store.setConnectorMultiAgentIterationResults(iterationResults);
      store.setConnectorMultiAgentIsOptimized(iterationResults.length > 0);
      store.setConnectorMultiAgentSelectedIterationIndex(0);
    } else {
      store.setConnectorMultiAgentIterationResults([]);
      store.setConnectorMultiAgentIsOptimized(false);
    }

    store.setConnectorMultiAgentOptimizedPrompts([]);

    stashConnectorPendingConfig(cfg);

    if (integrationId) {
      navigate(`/connector?integrationId=${integrationId}`);
    } else {
      navigate('/connector');
    }
    setNotification({
      isVisible: true,
      type: 'success',
      message: 'Full optimization loaded into Connector (initial configuration + all optimized steps).',
    });
  };

  const handleLoadToConnector = (
    coordinatorData: any,
    workersData: any[],
    metaPromptsData?: any,
    integrationId?: string | null,
    configLabel?: string,
    reminderPromptPositionRaw?: unknown
  ) => {
    if (!coordinatorData) {
      setNotification({ isVisible: true, type: 'error', message: OPTIMIZATION_COORDINATOR_MISSING_MESSAGE });
      return;
    }
    const cfg = buildAgentConfigFromData(
      coordinatorData,
      workersData,
      metaPromptsData,
      reminderPromptPositionRaw
    );
    const store = useOptimizationStore.getState();
    store.setConnectorCoordinatorPrompt(cfg.coordinator.prompt);
    store.setConnectorCoordinatorModel(cfg.coordinator.model);
    store.setConnectorCoordinatorTemperature(cfg.coordinator.temperature);
    store.setConnectorCoordinatorDescription(cfg.coordinator.description);
    store.setConnectorCoordinatorReminderPrompt(cfg.coordinator.reminder_prompt || '');
    store.setConnectorCoordinatorBackendId(
      typeof cfg.coordinator.id === 'string' ? cfg.coordinator.id.trim() || undefined : undefined
    );
    store.setConnectorCoordinatorAgentId(
      typeof cfg.coordinator.agent_id === 'string'
        ? cfg.coordinator.agent_id.trim() || undefined
        : typeof cfg.coordinator.id === 'string'
          ? cfg.coordinator.id.trim() || undefined
          : undefined
    );
    store.setConnectorReminderPromptPosition(cfg.reminder_prompt_position ?? 'before');
    store.setConnectorConfigLabel(configLabel?.trim() || undefined);
    store.setConnectorWorkers(
      cfg.workers.map((w, i) => ({
        id: normalizeWorkerIdString(w.id, i),
        name: w.name,
        prompt: w.prompt,
        model: w.model,
        temperature: w.temperature,
        description: w.description,
        mode: w.mode ?? 'synchronous',
        reminder_prompt: w.reminder_prompt,
      }))
    );
    store.setConnectorMetaPrompts(mergeMetaPromptsWithFinalStepDefault(cfg.metaPrompts));

    stashConnectorPendingConfig(cfg);

    // Очищаем данные оптимизации, чтобы не показывались графики/Optimized configuration от прошлых запусков
    store.setConnectorMultiAgentIterationResults([]);
    store.setConnectorMultiAgentOptimizedPrompts([]);
    store.setConnectorMultiAgentIsOptimized(false);

    if (integrationId) {
      navigate(`/connector?integrationId=${integrationId}`);
    } else {
      navigate('/connector');
    }
    setNotification({ isVisible: true, type: 'success', message: 'Configuration loaded into Connector' });
  };

  const handleLoadStepToConnector = (step: OptimizationStep) => {
    const masterAgent = extractCoordinatorFromData(step.data);
    const workerAgents = extractWorkersFromData(step.data);
    const metaPrompts = extractMetaPromptsFromData(step.data);

    if (!masterAgent) {
      setNotification({
        isVisible: true,
        type: 'error',
        message: `Step ${step.step_number}: ${OPTIMIZATION_COORDINATOR_MISSING_MESSAGE}`,
      });
      return;
    }

    const runLabel = (selectedHistoryRunConfigLabel ?? '').trim();
    const configLabel =
      runLabel ||
      (selectedConfig as any)?.label ||
      (selectedConfig as any)?.config_label ||
      (selectedConfig as any)?.configLabel ||
      undefined;
    const stepPayload = step.data as Record<string, unknown> | undefined;
    const rp =
      stepPayload?.reminder_prompt_position ?? stepPayload?.reminderPromptPosition;
    handleLoadToConnector(masterAgent, workerAgents, metaPrompts, selectedIntegrationId, configLabel, rp);
  };

  const goToOptimizer = () => {
    navigate('/connector');
  };

  const handleLoadInitialConfigToConnector = () => {
    if (!selectedConfig) return;

    const step0 = selectedConfigSteps?.find(s => s.step_number === 0);
    const masterAgent = extractCoordinatorFromConfig(selectedConfig) || extractCoordinatorFromData(step0?.data);
    const workerAgents = (() => {
      const fromConfig = extractWorkersFromConfig(selectedConfig);
      return fromConfig.length > 0 ? fromConfig : extractWorkersFromData(step0?.data);
    })();
    const metaPrompts = (selectedConfig as any)?.metaPrompts || (selectedConfig as any)?.meta_prompts || extractMetaPromptsFromData(step0?.data);

    if (!masterAgent) {
      setNotification({ isVisible: true, type: 'error', message: OPTIMIZATION_COORDINATOR_MISSING_MESSAGE });
      return;
    }

    const runLabel = (selectedHistoryRunConfigLabel ?? '').trim();
    const configLabel =
      runLabel ||
      (selectedConfig as any)?.label ||
      (selectedConfig as any)?.config_label ||
      (selectedConfig as any)?.configLabel ||
      undefined;
    const step0Payload = step0?.data as Record<string, unknown> | undefined;
    const rp0 = step0Payload?.reminder_prompt_position ?? step0Payload?.reminderPromptPosition;
    handleLoadToConnector(
      masterAgent,
      workerAgents,
      metaPrompts,
      selectedIntegrationId,
      configLabel,
      rp0
    );
    setShowConfigModal(false);
  };

  const handleLoadToMultiAgent = (
    coordinatorData: any,
    workersData: any[] = []
  ) => {
    if (!coordinatorData) {
      setNotification({ isVisible: true, type: 'error', message: OPTIMIZATION_COORDINATOR_MISSING_MESSAGE });
      return;
    }
    const store = useOptimizationStore.getState();
    const coordinatorPrompt = coordinatorData.prompt || '';
    const coordinatorModel = coordinatorData.model_name || coordinatorData.model || '';
    const coordinatorTemperature = coordinatorData.temperature ?? 0;
    const coordinatorDescription = coordinatorData.description || '';
    const storeWorkers = workersData.map((agent, index) => ({
      id: agent.id || index + 1,
      name: agent.name || `Worker ${index + 1}`,
      prompt: agent.prompt || '',
      model: agent.model_name || agent.model || '',
      temperature: agent.temperature ?? 0,
      description: agent.description || '',
      mode: agent.mode || 'asynchronous' as 'synchronous' | 'asynchronous',
      use_rag: agent.use_rag || false,
      use_ltm: agent.use_ltm || false,
    }));

    store.setCoordinatorPrompt(coordinatorPrompt);
    store.setCoordinatorModel(coordinatorModel);
    store.setCoordinatorTemperature(coordinatorTemperature);
    store.setCoordinatorDescription(coordinatorDescription);
    store.setWorkers(storeWorkers);
    store.setMultiAgentIterationResults([]);
    store.setMultiAgentOptimizedPrompts([]);
    store.setMultiAgentIsOptimized(false);

    navigate('/multi-agent');
    setNotification({ isVisible: true, type: 'success', message: 'Configuration loaded into Multi-Agent Optimization' });
  };

  const handleLoadStepToMultiAgent = (step: OptimizationStep) => {
    const coordinator = extractCoordinatorFromData(step.data);
    const workers = extractWorkersFromData(step.data);
    if (!coordinator) {
      setNotification({
        isVisible: true,
        type: 'error',
        message: `Step ${step.step_number}: ${OPTIMIZATION_COORDINATOR_MISSING_MESSAGE}`,
      });
      return;
    }
    handleLoadToMultiAgent(coordinator, workers);
  };

  const handleLoadInitialConfigToMultiAgent = () => {
    if (!selectedConfig) return;
    const step0 = selectedConfigSteps?.find(s => s.step_number === 0);
    const coordinator = extractCoordinatorFromConfig(selectedConfig) || extractCoordinatorFromData(step0?.data);
    const workers = (() => {
      const fromConfig = extractWorkersFromConfig(selectedConfig);
      return fromConfig.length > 0 ? fromConfig : extractWorkersFromData(step0?.data);
    })();

    if (!coordinator) {
      setNotification({ isVisible: true, type: 'error', message: OPTIMIZATION_COORDINATOR_MISSING_MESSAGE });
      return;
    }
    handleLoadToMultiAgent(coordinator, workers);
    setShowConfigModal(false);
  };

  const handleViewConfig = (
    config: OptimizationHistoryItem['configuration'],
    steps?: OptimizationHistoryItem['steps'],
    integrationId?: string,
    runConfigLabel?: string | null
  ) => {
    setSelectedConfig(config ?? ({} as OptimizationHistoryItem['configuration']));
    setSelectedConfigSteps(steps || null);
    setSelectedIntegrationId(integrationId || null);
    setSelectedHistoryRunConfigLabel(resolveHistoryRunConfigLabel(runConfigLabel, config ?? null));
    setShowConfigModal(true);
  };
  
  const handleLoadToIntegration = async (item: OptimizationHistoryItem) => {
    // Multi-agent не поддерживает интеграцию
    setNotification({
      isVisible: true,
      type: 'info',
      message: 'Integration is not available for multi-agent optimizations'
    });
  };


  return (
    <>
      <FloatingIcons />
      
      <div className="container">
        <div className="profile-root">
          <aside className="profile-sidebar" id="profile-sidebar">
            <button
              id="profile-tab-profile"
              type="button"
              className={`sidebar-btn${tab === 'profile' ? ' active' : ''}`}
              onClick={() => {
                setNotification((prev) => ({ ...prev, isVisible: false }));
                setSaveClicked(false);
                setTab('profile');
              }}
            >
              Profile <span className="sidebar-dot" />
            </button>
            <button
              id="profile-tab-history"
              type="button"
              className={`sidebar-btn${tab === 'history' ? ' active' : ''}`}
              onClick={() => {
                setNotification((prev) => ({ ...prev, isVisible: false }));
                setSaveClicked(false);
                setTab('history');
              }}
            >
              History <span className="sidebar-dot" />
            </button>
            <button
              id="profile-tab-usage"
              type="button"
              className={`sidebar-btn${tab === 'usage' ? ' active' : ''}`}
              onClick={() => {
                setNotification((prev) => ({ ...prev, isVisible: false }));
                setSaveClicked(false);
                setTab('usage');
              }}
            >
              Usage <span className="sidebar-dot" />
            </button>
            <button
              id="profile-tab-models"
              type="button"
              className={`sidebar-btn${tab === 'models' ? ' active' : ''}`}
              onClick={() => {
                setNotification((prev) => ({ ...prev, isVisible: false }));
                setSaveClicked(false);
                setTab('models');
              }}
            >
              Models <span className="sidebar-dot" />
            </button>
            <button
              id="profile-tab-fine-tuning"
              type="button"
              className={`sidebar-btn${tab === 'fine-tuning' ? ' active' : ''}`}
              onClick={() => {
                setNotification((prev) => ({ ...prev, isVisible: false }));
                setSaveClicked(false);
                setTab('fine-tuning');
              }}
            >
              Fine-tuning <span className="sidebar-dot" />
            </button>
          </aside>
          <main className="profile-main" id="profile-main">
            <div className="profile-main-toolbar" id="profile-onboarding-toolbar">
              <HelpTourButton
                onClick={() => {
                  setNotification((prev) => ({ ...prev, isVisible: false }));
                  setSaveClicked(false);
                  setTab('profile');
                  setShowProfileOnboarding(true);
                }}
              />
            </div>
            {tab === 'profile' ? (
              <div className="profile-card">
                <form className="profile-form" onSubmit={handleSave}>
                  <h2 className="profile-title">Profile</h2>
                  <div id="profile-onboarding-identity">
                  <div className="profile-field">
                    <label>
                      Name <span className="required">*</span>
                    </label>
                    <div className={`input-icon-wrap${nameRequiredError ? ' input-error' : ''}`}>
                      <i className="fas fa-user input-icon"></i>
                      <input 
                        type="text" 
                        placeholder="Enter your name" 
                        value={name} 
                        onChange={handleNameChange}
                        disabled={isLoading}
                        ref={nameInputRef}
                      />
                    </div>
                    <div className="profile-field-footer">
                      <span className="char-counter">
                        {name.length}/{NAME_MAX_LENGTH}
                      </span>
                      {hasNameLimitWarning && (
                        <span className="field-hint">Maximum length is {NAME_MAX_LENGTH} characters</span>
                      )}
                      {!hasNameLimitWarning && nameRequiredError && (
                        <span className="field-error">Name is required</span>
                      )}
                    </div>
                  </div>
                  <div className="profile-field">
                    <label>Company Name <span className="optional">(optional)</span></label>
                    <div className="input-icon-wrap">
                      <i className="fas fa-building input-icon"></i>
                      <input 
                        type="text" 
                        placeholder="Enter your company name (optional)" 
                        value={company} 
                        onChange={handleCompanyChange}
                        disabled={isLoading}
                      />
                    </div>
                    <div className="profile-field-footer">
                      <span className="char-counter">
                        {company.length}/{COMPANY_MAX_LENGTH}
                      </span>
                      {hasCompanyLimitWarning && (
                        <span className="field-hint">Maximum length is {COMPANY_MAX_LENGTH} characters</span>
                      )}
                    </div>
                  </div>
                  </div>
                  <div id="profile-onboarding-preferences" className="profile-checkbox">
                    <input 
                      type="checkbox" 
                      id="comm" 
                      checked={comm} 
                      onChange={e => setComm(e.target.checked)}
                      disabled={isLoading}
                    />
                    <label htmlFor="comm" style={{ cursor: 'pointer' }}>I am interested in receiving communication from us</label>
                  </div>
                  <div id="profile-onboarding-save" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 16 }}>
                    <button ref={saveButtonRef} className="profile-save-btn" type="submit" disabled={isLoading} onClick={() => setSaveClicked(true)}>
                      {isLoading ? 'Saving...' : 'Save'}
                    </button>
                    
                    {/* Уведомление рядом с кнопкой */}
                    {notification.isVisible && saveClicked ? (
                      <Notification
                        type={notification.type}
                        message={notification.message}
                        isVisible={true}
                        onClose={() => { setNotification(prev => ({ ...prev, isVisible: false })); setSaveClicked(false); }}
                        position="inline"
                        targetElement={saveButtonRef.current}
                        duration={NOTIFICATION_AUTO_HIDE_MS}
                      />
                    ) : null}
                  </div>
                </form>
              </div>
            ) : tab === 'history' ? (
              <div className="profile-history-card" id="profile-history-section">
                <div className="history-content">
                  <div className="history-header">
                    <h2 className="profile-title">Optimization History</h2>
                    <div className="history-category-tabs" id="profile-onboarding-history-categories">
                      <button
                        className={`history-category-tab ${historyCategory === 'integrations' ? 'active' : ''}`}
                        onClick={() => setHistoryCategory('integrations')}
                      >
                        Integrations
                      </button>
                      <button
                        className={`history-category-tab ${historyCategory === 'multi-agent' ? 'active' : ''}`}
                        onClick={() => setHistoryCategory('multi-agent')}
                      >
                        Multi-Agent
                      </button>
                    </div>
                  </div>
                  {/* Уведомления для истории (не показываем уведомление о сохранении профиля) */}
                  {notification.isVisible && tab === 'history' && notification.message !== 'Profile saved successfully!' && (
                    <div style={{ marginBottom: '16px' }}>
                      <Notification
                        type={notification.type}
                        message={notification.message}
                        isVisible={true}
                        onClose={() => setNotification(prev => ({ ...prev, isVisible: false }))}
                        position="inline"
                        duration={NOTIFICATION_AUTO_HIDE_MS}
                      />
                    </div>
                  )}
                  <div className={`profile-history-container${isHistoryEmpty ? ' is-empty' : ''}`}>
                    {isHistoryLoading ? (
                      <div className="loading-message">Loading history...</div>
                    ) : !currentHistory || currentHistory.length === 0 ? (
                      <div className="history-empty-state">
                        <div className="history-empty-icon" aria-hidden="true">
                          <i className="fas fa-history"></i>
                        </div>
                        <h3 className="history-empty-title">No optimization history yet</h3>
                        <p className="history-empty-text">
                          When you run an optimization, it will appear here so you can review results and reuse the best prompt later.
                        </p>
                        <div className="history-empty-hint">
                          To get started:
                          <ul className="history-empty-list">
                            <li>Open the optimizer</li>
                            <li>Run your first optimization</li>
                            <li>Come back here to review the run</li>
                          </ul>
                        </div>
                        <div className="history-empty-actions">
                          <button
                            type="button"
                            className="history-empty-btn primary"
                            onClick={() => goToOptimizer()}
                          >
                            Start first optimization
                          </button>
                        </div>
                      </div>
                    ) : (
                      <>
                        <div className="profile-history-table-wrap">
                          <table className="profile-history-table">
                            <thead>
                              <tr>
                                <th>Configuration</th>
                                <th>Steps</th>
                                {historyCategory === 'integrations' && <th>Score</th>}
                                <th>Date</th>
                                <th>Actions</th>
                              </tr>
                            </thead>
                            <tbody>
                              {currentHistory.map((item, index) => {
                                // Подсчитываем только оптимизированные шаги (step_number > 0)
                                const optimizedStepsCount = item.steps?.filter(step => step.step_number > 0).length || 0;
                                
                                // Определяем формат конфигурации
                                const masterAgent = item.configuration?.["master-agent"];
                                const workerAgents = item.configuration?.["worker-agents"] || [];
                                const coordinator = (item.configuration as any)?.coordinator;
                                const workers = (item.configuration as any)?.workers || [];
                                
                                // Проверяем в step.data для integrations
                                const step0 = item.steps?.find(step => step.step_number === 0);
                                const step0Coordinator = step0?.data?.["master-agent"] || (step0?.data as any)?.coordinator;
                                const step0Workers = step0?.data?.["worker-agents"] || (step0?.data as any)?.workers || [];
                                
                                // Определяем финальный формат
                                const finalCoordinator = coordinator || masterAgent || step0Coordinator;
                                const finalWorkers = workers.length > 0 ? workers : (workerAgents.length > 0 ? workerAgents : step0Workers);
                                const isMultiAgent = !!finalCoordinator;
                                const isSingleAgent = !isMultiAgent && !!item.configuration?.initial_prompt;
                                
                                // Извлекаем score из последнего оптимизированного step (если есть)
                                // Ищем последний step с evaluation_point
                                const optimizedSteps = item.steps?.filter(step => step.step_number > 0 && step.evaluation_point) || [];
                                const lastOptimizedStep = optimizedSteps.length > 0 
                                  ? optimizedSteps[optimizedSteps.length - 1] 
                                  : null;
                                const score = lastOptimizedStep?.evaluation_point?.y ?? null;
                                
                                return (
                                  <tr 
                                    key={item.id || index}
                                    title={`Experiment ID: ${item.id}`}
                                    style={{ cursor: 'pointer' }}
                                    onMouseEnter={(e) => {
                                      e.currentTarget.style.backgroundColor = '#F5F9FE';
                                    }}
                                    onMouseLeave={(e) => {
                                      e.currentTarget.style.backgroundColor = '';
                                    }}
                                  >
                                    <td>
                                      <div className="history-open-cell history-open-cell--config" style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                        {(item.config_label ?? '').trim() ? (
                                          <div
                                            className="profile-history-run-config-label"
                                            title={String(item.config_label)}
                                            style={{ fontSize: 13, fontWeight: 600, color: '#0A2A47', lineHeight: 1.35 }}
                                          >
                                            {item.config_label}
                                          </div>
                                        ) : null}
                                        {isMultiAgent ? (
                                          <div className="config-preview" style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center' }}>
                                            <span className="agent-badge">
                                              Coordinator: {finalCoordinator?.name?.trim() || <HistoryFieldEmpty>Unnamed</HistoryFieldEmpty>}
                                            </span>
                                            <span className="agent-badge">{finalWorkers.length} Workers</span>
                                          </div>
                                        ) : isSingleAgent ? (
                                          <div className="config-preview" style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center' }}>
                                            <span className="agent-badge">Single-agent</span>
                                            {(item.configuration?.model_name || '').trim() ? (
                                              <span className="agent-badge">{item.configuration?.model_name}</span>
                                            ) : (
                                              <span className="agent-badge agent-badge--legacy">Model N/A</span>
                                            )}
                                          </div>
                                        ) : (
                                          <div className="config-preview" style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center' }}>
                                            <span className="agent-badge agent-badge--legacy">Incomplete / legacy</span>
                                            <span className="history-inline-hint">Limited metadata</span>
                                          </div>
                                        )}
                                        <button 
                                          className="view-config-btn"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            handleViewConfig(item.configuration, item.steps, item.integration_id, item.config_label);
                                          }}
                                          title={isMultiAgent ? "View multi-agent configuration" : "View configuration"}
                                          style={{ alignSelf: 'flex-start' }}
                                        >
                                          <i className="fas fa-cog"></i> Open
                                        </button>
                                      </div>
                                    </td>
                                    <td>
                                      <div className="history-open-cell history-open-cell--steps" style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                        <span className="steps-preview">
                                          {optimizedStepsCount} optimized steps
                                          {(!item.steps || item.steps.length === 0) && (
                                            <span className="history-steps-hint">No step payloads in save</span>
                                          )}
                                        </span>
                                        {(item.steps?.length || item.configuration != null) ? (
                                          <button 
                                            className="view-steps-btn"
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              handleViewSteps(
                                                item.steps || [],
                                                item.selected_step_id,
                                                item.configuration,
                                                item.id,
                                                item.integration_id,
                                                item.config_label
                                              );
                                            }}
                                            title="View all optimization steps"
                                            style={{ alignSelf: 'flex-start' }}
                                          >
                                            <i className="fas fa-eye"></i> Open
                                          </button>
                                        ) : (
                                          <span className="history-inline-hint" style={{ alignSelf: 'flex-start' }}>Nothing to open</span>
                                        )}
                                      </div>
                                    </td>
                                    {historyCategory === 'integrations' && (
                                      <td>{formatScore(score)}</td>
                                    )}
                                    <td>{formatDateShort(item.created_at)}</td>
                                    <td>
                                      <div className="actions-buttons" style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                                        {optimizedStepsCount > 0 && (
                                          <button
                                            className="export-step-btn"
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              if (historyCategory === 'integrations') {
                                                handleLoadFullOptimizationToConnector(item);
                                              } else {
                                                handleViewHistory(item);
                                              }
                                            }}
                                            title={historyCategory === 'integrations'
                                              ? 'Load initial configuration and all optimized steps into Connector'
                                              : 'Load configuration into Multi-Agent Optimization'}
                                            style={{
                                              padding: '6px 12px',
                                              fontSize: '12px',
                                              background: '#FF8300',
                                              color: 'white',
                                              border: 'none',
                                              borderRadius: '8px',
                                              cursor: 'pointer',
                                              display: 'flex',
                                              alignItems: 'center',
                                              gap: '10px'
                                            }}
                                          >
                                            <i className="fas fa-upload" aria-hidden /> {historyCategory === 'integrations' ? 'Load to Connector' : 'Load into Multi-Agent'}
                                          </button>
                                        )}
                                      </div>
                                    </td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>
                        
                        {/* Карточный вид для мобильных устройств */}
                        <div id="profile-onboarding-history-table" className="profile-history-cards">
                          {currentHistory.map((item, index) => {
                            // Подсчитываем только оптимизированные шаги (step_number > 0)
                            const optimizedStepsCount = item.steps?.filter(step => step.step_number > 0).length || 0;
                            const masterAgent = item.configuration?.['master-agent'];
                            const workerAgents = item.configuration?.['worker-agents'] || [];
                            const coord = (item.configuration as any)?.coordinator;
                            const workers = (item.configuration as any)?.workers || [];
                            const step0 = item.steps?.find((step) => step.step_number === 0);
                            const step0Coord = step0?.data?.['master-agent'] || (step0?.data as any)?.coordinator;
                            const step0Workers = step0?.data?.['worker-agents'] || (step0?.data as any)?.workers || [];
                            const finalCoord = coord || masterAgent || step0Coord;
                            const finalWorkers =
                              workers.length > 0 ? workers : workerAgents.length > 0 ? workerAgents : step0Workers;
                            const isMultiAgentCard = !!finalCoord;
                            const isSingleAgentCard = !isMultiAgentCard && !!item.configuration?.initial_prompt;
                            const createdTs = item.created_at ? Date.parse(item.created_at) : NaN;
                            
                            return (
                              <div key={item.id || index} className="profile-history-card-item">
                                <div className="profile-history-card-header">
                                  <div className="profile-history-card-date">
                                    {!Number.isFinite(createdTs) ? (
                                      <HistoryFieldEmpty>—</HistoryFieldEmpty>
                                    ) : (
                                      new Date(createdTs).toLocaleString('ru-RU', {
                                        day: '2-digit',
                                        month: '2-digit',
                                        year: 'numeric',
                                        hour: '2-digit',
                                        minute: '2-digit',
                                      })
                                    )}
                                  </div>
                                </div>
                                
                                <div className="profile-history-card-content">
                                  <div className="profile-history-card-field">
                                    <div className="profile-history-card-label">Config name</div>
                                    <div className="profile-history-card-value">
                                      {(item.config_label ?? '').trim() ? (
                                        <span className="profile-history-config-label" title={String(item.config_label)}>
                                          {item.config_label}
                                        </span>
                                      ) : (
                                        <HistoryFieldEmpty>—</HistoryFieldEmpty>
                                      )}
                                    </div>
                                  </div>
                                  <div className="profile-history-card-field profile-history-card-field--config">
                                    <div className="profile-history-card-label">Configuration</div>
                                    <div className="profile-history-card-value profile-history-card-value--config">
                                      <button 
                                        className="view-config-btn"
                                        onClick={() => handleViewConfig(item.configuration, item.steps, item.integration_id, item.config_label)}
                                        title={masterAgent ? 'View multi-agent configuration' : 'View configuration'}
                                      >
                                        <i className="fas fa-cog"></i> Open
                                      </button>
                                      {isMultiAgentCard ? (
                                        <div className="config-preview profile-history-card-meta">
                                          <span className="agent-badge">
                                            Coordinator:{' '}
                                            {finalCoord?.name?.trim() || <HistoryFieldEmpty>Unnamed</HistoryFieldEmpty>}
                                          </span>
                                          <span className="agent-badge">{finalWorkers.length} Workers</span>
                                        </div>
                                      ) : isSingleAgentCard ? (
                                        <div className="config-preview profile-history-card-meta">
                                          <span className="agent-badge">Single-agent</span>
                                          {(item.configuration?.model_name || '').trim() ? (
                                            <span className="agent-badge">{item.configuration?.model_name}</span>
                                          ) : (
                                            <span className="agent-badge agent-badge--legacy">Model N/A</span>
                                          )}
                                        </div>
                                      ) : (
                                        <div className="config-preview profile-history-card-meta">
                                          <span className="agent-badge agent-badge--legacy">Incomplete / legacy</span>
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                  
                                  <div className="profile-history-card-field profile-history-card-field--steps">
                                    <div className="profile-history-card-label">Steps</div>
                                    <div className="profile-history-card-value profile-history-card-value--steps">
                                      <span className="profile-history-card-text">
                                        {optimizedStepsCount} optimized steps
                                        {(!item.steps || item.steps.length === 0) && (
                                          <span className="history-steps-hint"> · no payloads</span>
                                        )}
                                      </span>
                                      {item.steps?.length || item.configuration != null ? (
                                        <button 
                                          className="view-steps-btn"
                                          onClick={() =>
                                            handleViewSteps(
                                              item.steps || [],
                                              item.selected_step_id,
                                              item.configuration,
                                              item.id,
                                              item.integration_id,
                                              item.config_label
                                            )
                                          }
                                          title="View all optimization steps"
                                        >
                                          <i className="fas fa-eye"></i> Open
                                        </button>
                                      ) : (
                                        <span className="history-inline-hint">Nothing to open</span>
                                      )}
                                    </div>
                                  </div>
                                  
                                  <div className="profile-history-card-field">
                                    <div className="profile-history-card-label">Iterations</div>
                                    <div className="profile-history-card-value">
                                      {renderHistoryIterations(item.configuration?.iterations)}
                                    </div>
                                  </div>
                                  
                                  <div className="profile-history-card-field profile-history-card-field--dataset">
                                    <div className="profile-history-card-label">Dataset</div>
                                    <div className="profile-history-card-value">
                                      {formatOptimizationDatasetDisplay(item.configuration?.dataset_name) ? (
                                        <span
                                          className="profile-history-dataset-name"
                                          title={formatOptimizationDatasetDisplay(item.configuration?.dataset_name)}
                                        >
                                          {formatOptimizationDatasetDisplay(item.configuration?.dataset_name)}
                                        </span>
                                      ) : (
                                        <HistoryFieldEmpty>—</HistoryFieldEmpty>
                                      )}
                                    </div>
                                  </div>
                                </div>
                                
                                <div className="profile-history-card-actions">
                                  {optimizedStepsCount > 0 && (() => {
                                    return (
                                      <button 
                                        className="profile-view-btn"
                                        onClick={() => {
                                          if (historyCategory === 'integrations') {
                                            handleLoadFullOptimizationToConnector(item);
                                          } else {
                                            handleViewHistory(item);
                                          }
                                        }}
                                        title={historyCategory === 'integrations'
                                          ? 'Load initial configuration and all optimized steps into Connector'
                                          : 'Load configuration into Multi-Agent Optimization'}
                                        style={{ background: '#FF8300', borderColor: '#FF8300' }}
                                      >
                                        <i className="fas fa-upload" aria-hidden /> {historyCategory === 'integrations' ? 'Load to Connector' : 'Load into Multi-Agent'}
                                      </button>
                                    );
                                  })()}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                        {historyPagination ? (
                          <div
                            style={{
                              marginTop: 16,
                              display: 'flex',
                              flexWrap: 'wrap',
                              alignItems: 'center',
                              justifyContent: 'space-between',
                              gap: 12,
                            }}
                          >
                            <span style={{ color: '#5B7893', fontSize: 14 }}>
                              Loaded {allHistoryItems.length} of {historyPagination.total} runs
                              {currentHistory.length !== allHistoryItems.length
                                ? ` · ${currentHistory.length} in ${historyCategory === 'integrations' ? 'Integrations' : 'Multi-Agent'}`
                                : ''}
                            </span>
                            {historyHasMore ? (
                              <button
                                type="button"
                                className="history-category-tab active"
                                disabled={historyLoadingMore || isHistoryLoading}
                                onClick={() => void loadHistory({ append: true })}
                                style={{ minWidth: 200 }}
                              >
                                {historyLoadingMore ? 'Loading…' : 'Load more'}
                              </button>
                            ) : null}
                          </div>
                        ) : null}
                      </>
                    )}
                  </div>
                </div>
              </div>
            ) : tab === 'models' ? (
              <div className="profile-card" id="profile-models-section" style={{ alignItems: 'stretch', width: '100%' }}>
                <h2 className="profile-title">
                  <i className="fas fa-cube" style={{ marginRight: 8, color: '#FF8300' }}></i>
                  Models for Configurator
                </h2>
                <p style={{ color: '#5B7893', fontSize: 14, marginBottom: 20 }}>
                  Choose which models appear in the Connector configurator for each integration. Only selected models will be available when configuring agents.
                </p>
                <div className="profile-field" style={{ marginBottom: 16 }}>
                  <label htmlFor="models-integration-picker-trigger">Integration</label>
                  <div
                    id="profile-onboarding-models-integration"
                    ref={modelsIntegrationPickerRef}
                    style={{ position: 'relative', width: '100%', maxWidth: '100%' }}
                  >
                    <button
                      id="models-integration-picker-trigger"
                      type="button"
                      aria-haspopup="listbox"
                      aria-expanded={modelsIntegrationPickerOpen}
                      onClick={() => {
                        setModelsIntegrationPickerOpen((o) => {
                          const next = !o;
                          if (next) setModelsIntegrationSearch('');
                          return next;
                        });
                      }}
                      style={{
                        width: '100%',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 10,
                        padding: '10px 12px',
                        borderRadius: 8,
                        border: `1px solid ${modelsIntegrationPickerOpen ? '#FF8300' : '#E3F0FB'}`,
                        background: '#FFFFFF',
                        cursor: 'pointer',
                        textAlign: 'left',
                        boxSizing: 'border-box',
                        boxShadow: modelsIntegrationPickerOpen ? '0 0 0 2px rgba(255, 131, 0, 0.2)' : undefined,
                      }}
                    >
                      <div style={{ flex: 1, minWidth: 0 }}>
                        {selectedModelsIntegrationDisplay ? (
                          <>
                            <div
                              style={{
                                fontWeight: 600,
                                fontSize: 14,
                                color: '#0A2A47',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                              }}
                              title={
                                selectedModelsIntegrationDisplay.secondary !==
                                selectedModelsIntegrationDisplay.primary
                                  ? selectedModelsIntegrationDisplay.secondary
                                  : undefined
                              }
                            >
                              {selectedModelsIntegrationDisplay.primary}
                            </div>
                            {selectedModelsIntegrationDisplay.secondary !==
                              selectedModelsIntegrationDisplay.primary && (
                              <div
                                style={{
                                  fontSize: 12,
                                  color: '#5B7893',
                                  marginTop: 2,
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap',
                                }}
                                title={selectedModelsIntegrationDisplay.secondary}
                              >
                                {selectedModelsIntegrationDisplay.secondary}
                              </div>
                            )}
                          </>
                        ) : (
                          <span style={{ fontSize: 14, color: '#7F9AB3' }}>Select integration</span>
                        )}
                      </div>
                      <i
                        className={`fas fa-chevron-down${modelsIntegrationPickerOpen ? ' open' : ''}`}
                        style={{
                          flexShrink: 0,
                          fontSize: 12,
                          color: '#5B7893',
                          transition: 'transform 0.2s ease',
                          transform: modelsIntegrationPickerOpen ? 'rotate(180deg)' : undefined,
                        }}
                        aria-hidden
                      />
                    </button>
                    {modelsIntegrationPickerOpen && (
                      <div
                        role="listbox"
                        style={{
                          position: 'absolute',
                          left: 0,
                          right: 0,
                          top: '100%',
                          marginTop: 6,
                          background: '#FFFFFF',
                          border: '1px solid #E3F0FB',
                          borderRadius: 10,
                          boxShadow: '0 0 0 1px #E3F0FB',
                          zIndex: 50,
                          overflow: 'hidden',
                        }}
                      >
                        <div style={{ padding: 8, borderBottom: '1px solid #F5F9FE' }}>
                          <input
                            type="search"
                            placeholder="Search by name, domain, or URL…"
                            value={modelsIntegrationSearch}
                            onChange={(e) => setModelsIntegrationSearch(e.target.value)}
                            onClick={(e) => e.stopPropagation()}
                            autoComplete="off"
                            autoFocus
                            aria-label="Search integrations"
                            style={{
                              width: '100%',
                              padding: '8px 10px',
                              borderRadius: 8,
                              border: '1px solid #E3F0FB',
                              fontSize: 14,
                              boxSizing: 'border-box',
                            }}
                          />
                        </div>
                        <div
                          style={{
                            maxHeight: 'min(72vh, 560px)',
                            overflowY: 'auto',
                            WebkitOverflowScrolling: 'touch',
                          }}
                        >
                          {filteredModelsIntegrations.length === 0 ? (
                            <div style={{ padding: 16, fontSize: 13, color: '#7F9AB3', textAlign: 'center' }}>
                              No matching integrations
                            </div>
                          ) : (
                            filteredModelsIntegrations.map((i) => {
                              const row = getModelsIntegrationDisplay(i);
                              const isSel = i.id === modelsSelectedIntegrationId;
                              return (
                                <button
                                            key={i.id}
                                            type="button"
                                            role="option"
                                            aria-selected={isSel}
                                            title={
                                              row.secondary !== row.primary ? row.secondary : row.primary
                                            }
                                  onClick={() => {
                                    setModelsSelectedIntegrationId(i.id);
                                    setStoredSelectedIntegrationId('connector', user?.id, i.id);
                                    setModelsIntegrationPickerOpen(false);
                                    setModelsIntegrationSearch('');
                                  }}
                                  style={{
                                    width: '100%',
                                    textAlign: 'left',
                                    padding: '10px 12px',
                                    border: 'none',
                                    borderLeft: isSel ? '3px solid #FF8300' : '3px solid transparent',
                                    background: isSel ? '#F5F9FE' : '#FFFFFF',
                                    cursor: 'pointer',
                                    borderBottom: '1px solid #F5F9FE',
                                  }}
                                  onMouseEnter={(e) => {
                                    if (!isSel) e.currentTarget.style.background = '#FFFFFF';
                                  }}
                                  onMouseLeave={(e) => {
                                    if (!isSel) e.currentTarget.style.background = '#FFFFFF';
                                  }}
                                >
                                  <div
                                    style={{
                                      fontWeight: isSel ? 700 : 600,
                                      fontSize: 14,
                                      color: '#0A2A47',
                                      overflow: 'hidden',
                                      textOverflow: 'ellipsis',
                                      whiteSpace: 'nowrap',
                                    }}
                                  >
                                    {row.primary}
                                  </div>
                                  {row.secondary !== row.primary && (
                                    <div
                                      style={{
                                        fontSize: 12,
                                        color: '#5B7893',
                                        marginTop: 2,
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap',
                                      }}
                                    >
                                      {row.secondary}
                                    </div>
                                  )}
                                </button>
                              );
                            })
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
                {modelsError && (
                  <div style={{ padding: '10px 14px', background: '#FFFFFF', border: '1px solid #FFD9B3', borderRadius: 8, color: '#FF8300', fontSize: 13, marginBottom: 16 }}>
                    {modelsError}
                  </div>
                )}
                {modelsLoading && <div style={{ color: '#5B7893', marginBottom: 12 }}>Loading models...</div>}
                {!modelsLoading && modelsSelectedIntegrationId && (
                  <div id="profile-onboarding-models-toggles" style={{ marginTop: 16 }}>
                    <p style={{ fontSize: 13, color: '#4A6683', marginBottom: 10 }}>
                      Base models are always available in the Connector configurator:
                    </p>
                    <div
                      style={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        gap: 8,
                        marginBottom: 16,
                      }}
                    >
                      {DEFAULT_CONFIGURATOR_MODELS.map((modelId) => (
                        <span
                          key={modelId}
                          style={{
                            padding: '6px 10px',
                            borderRadius: 999,
                            background: '#F5F9FE',
                            border: '1px solid #E3F0FB',
                            fontSize: 12,
                            color: '#4A6683',
                          }}
                        >
                          {modelId}
                        </span>
                      ))}
                    </div>
                    <p style={{ fontSize: 13, color: '#4A6683', marginBottom: 10 }}>
                      Toggle which fine-tuned models are also available in the configurator:
                    </p>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                      {trainableModels.length === 0 ? (
                        <div style={{ padding: 24, textAlign: 'center', background: '#FFFFFF', borderRadius: 8, color: '#5B7893' }}>
                          No trainable models for this integration yet. Run fine-tuning first.
                        </div>
                      ) : (
                        trainableModels.map((m, rowIdx) => {
                          const modelId = trainableRowConfiguratorModelId(m);
                          const enabled = Boolean(modelId) && modelsConfiguratorIds.includes(modelId);
                          const agentId = resolveTrainableAgentId(m, integrationAgentsByName);
                          const canDeleteModel = Boolean(agentId) && trainableRowHasFineTunedModel(m);
                          const isDeletingModel = deletingTrainingAgentModelId === agentId;
                          return (
                            <div
                              key={modelId ? `${modelId}::${rowIdx}` : `trainable-row-${rowIdx}`}
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 12,
                                padding: '12px 14px',
                                background: enabled ? '#F5F9FE' : '#FFFFFF',
                                borderRadius: 8,
                                border: `1px solid ${enabled ? 'rgba(255, 131, 0, 0.25)' : '#E3F0FB'}`,
                              }}
                            >
                              <div style={{ flex: 1, minWidth: 0 }}>
                                <div style={{ fontWeight: 600, fontSize: 14, color: '#0A2A47' }}>{m.model_name || modelId}</div>
                                <div style={{ fontSize: 12, color: '#5B7893', marginTop: 2 }}>
                                  Base: {m.base_model}
                                  {m.agent_name ? ` · Agent: ${m.agent_name}` : ''}
                                </div>
                              </div>
                              {canDeleteModel && (
                                <button
                                  type="button"
                                  onClick={() => handleDeleteTrainingAgentModel(m)}
                                  disabled={isDeletingModel || modelsLoading}
                                  title="Delete fine-tuned model for this agent"
                                  style={{
                                    padding: '7px 10px',
                                    borderRadius: 8,
                                    border: '1px solid #FFD9B3',
                                    background: '#FFFFFF',
                                    color: '#B85400',
                                    fontSize: 12,
                                    fontWeight: 600,
                                    cursor: isDeletingModel || modelsLoading ? 'not-allowed' : 'pointer',
                                    opacity: isDeletingModel || modelsLoading ? 0.6 : 1,
                                    whiteSpace: 'nowrap',
                                  }}
                                >
                                  <i className={`fas ${isDeletingModel ? 'fa-spinner fa-spin' : 'fa-trash'}`} aria-hidden />{' '}
                                  {isDeletingModel ? 'Deleting…' : 'Delete model'}
                                </button>
                              )}
                              <div
                                role="switch"
                                aria-checked={enabled}
                                style={{
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  gap: 10,
                                  fontSize: 13,
                                  color: '#4A6683',
                                  cursor: 'pointer',
                                  userSelect: 'none',
                                }}
                                onClick={() => {
                                  if (!modelsSelectedIntegrationId || !modelId) return;
                                  const next = !enabled;
                                  const readableName =
                                    ftStr(m.model_name) || ftStr(m.agent_name) || ftStr(m.base_model);
                                  toggleConfiguratorModel(modelsSelectedIntegrationId, modelId, next, readableName);
                                  setModelsConfiguratorIds((prev) =>
                                    next
                                      ? Array.from(new Set([...prev, modelId]))
                                      : prev.filter((id) => id !== modelId)
                                  );
                                  setNotification({ isVisible: true, type: 'success', message: next ? 'Model added to configurator' : 'Model removed from configurator' });
                                }}
                              >
                                <div
                                  style={{
                                    position: 'relative',
                                    width: 48,
                                    height: 24,
                                    borderRadius: 12,
                                    background: enabled ? '#FF8300' : '#C6E0F7',
                                    transition: 'background 0.2s ease',
                                    cursor: 'pointer',
                                  }}
                                >
                                  <div
                                    style={{
                                      position: 'absolute',
                                      top: 2,
                                      left: enabled ? 26 : 2,
                                      width: 20,
                                      height: 20,
                                      borderRadius: '50%',
                                      background: '#FFFFFF',
                                      boxShadow: '0 0 0 1px #E3F0FB',
                                      transition: 'left 0.2s ease',
                                    }}
                                  />
                                </div>
                                <span>{enabled ? 'In configurator' : 'Not in configurator'}</span>
                              </div>
                            </div>
                          );
                        })
                      )}
                    </div>
                  </div>
                )}
              </div>
            ) : tab === 'fine-tuning' ? (
              <div className="profile-history-card" id="profile-fine-tuning-section">
                <div className="history-content">
                  <div className="history-header">
                    <h2 className="profile-title">Fine-tuning Jobs</h2>
                    <button
                      className="history-category-tab active"
                      onClick={() => {
                        setFineTuningSearch('');
                        setFineTuningStatusFilter('');
                        setFineTuningIntegrationFilter('');
                        void loadFineTuningHistory();
                        void loadIntegrationsMapForFineTuning();
                      }}
                      disabled={fineTuningLoading || fineTuningLoadingMore}
                      type="button"
                    >
                      {fineTuningLoading ? 'Refreshing…' : 'Refresh'}
                    </button>
                  </div>
                  {notification.isVisible && tab === 'fine-tuning' && notification.message !== 'Profile saved successfully!' && (
                    <div style={{ marginBottom: '16px' }}>
                      <Notification
                        type={notification.type}
                        message={notification.message}
                        isVisible={true}
                        onClose={() => setNotification(prev => ({ ...prev, isVisible: false }))}
                        position="inline"
                        duration={NOTIFICATION_AUTO_HIDE_MS}
                      />
                    </div>
                  )}
                  <div
                    className={`profile-history-container${!fineTuningLoading && fineTuningHistory.length === 0 ? ' is-empty' : ''}`}
                  >
                    {fineTuningError && (
                      <div style={{ marginBottom: 12 }}>
                        <Notification
                          type="error"
                          message={fineTuningError}
                          isVisible={true}
                          onClose={() => setFineTuningError(null)}
                          position="inline"
                          duration={NOTIFICATION_AUTO_HIDE_MS}
                        />
                      </div>
                    )}
                    {fineTuningLoading && fineTuningHistory.length === 0 ? (
                      <div className="loading-message">Loading fine-tuning jobs...</div>
                    ) : fineTuningHistory.length === 0 ? (
                      <div className="history-empty-state">
                        <div className="history-empty-icon" aria-hidden="true">
                          <i className="fas fa-graduation-cap"></i>
                        </div>
                        <h3 className="history-empty-title">No fine-tuning jobs yet</h3>
                        <p className="history-empty-text">Run a fine-tuning job in Connector, then manage it here.</p>
                      </div>
                    ) : (
                      <>
                        {fineTuningPagination && (
                          <p style={{ margin: '0 0 8px 0', color: '#5B7893', fontSize: 13 }}>
                            Loaded {fineTuningHistory.length} of {fineTuningPagination.total} jobs
                            {displayedFineTuningJobs.length !== fineTuningHistory.length &&
                              ` · Showing ${displayedFineTuningJobs.length} after filters`}
                          </p>
                        )}
                        <div
                          style={{
                            display: 'flex',
                            flexWrap: 'wrap',
                            gap: '10px',
                            alignItems: 'center',
                            marginBottom: 14,
                          }}
                        >
                          <input
                            type="search"
                            placeholder="Search model, integration, job id, status…"
                            value={fineTuningSearch}
                            onChange={(e) => setFineTuningSearch(e.target.value)}
                            aria-label="Search fine-tuning jobs"
                            style={{
                              flex: '1 1 220px',
                              minWidth: 180,
                              padding: '8px 12px',
                              borderRadius: 8,
                              border: '1px solid #C6E0F7',
                              fontSize: 14,
                            }}
                          />
                          <select
                            value={fineTuningStatusFilter}
                            onChange={(e) => setFineTuningStatusFilter(e.target.value)}
                            aria-label="Filter by status"
                            style={{
                              padding: '8px 12px',
                              borderRadius: 8,
                              border: '1px solid #C6E0F7',
                              fontSize: 14,
                              minWidth: 140,
                            }}
                          >
                            <option value="">All statuses</option>
                            {fineTuningStatusOptions.map((st) => (
                              <option key={st} value={st}>
                                {st}
                              </option>
                            ))}
                          </select>
                          <select
                            value={fineTuningIntegrationFilter}
                            onChange={(e) => setFineTuningIntegrationFilter(e.target.value)}
                            aria-label="Filter by integration"
                            style={{
                              padding: '8px 12px',
                              borderRadius: 8,
                              border: '1px solid #C6E0F7',
                              fontSize: 14,
                              minWidth: 180,
                              maxWidth: 280,
                            }}
                          >
                            <option value="">All integrations</option>
                            {fineTuningIntegrationOptions.map(({ id, label }) => (
                              <option key={id} value={id}>
                                {label}
                              </option>
                            ))}
                          </select>
                        </div>
                        {displayedFineTuningJobs.length === 0 && fineTuningHistory.length > 0 && (
                          <p style={{ margin: '0 0 12px 0', color: '#B85400', fontSize: 13 }}>
                            No jobs match the current search or filters. Clear or adjust the filters and try again.
                          </p>
                        )}
                        <div className="profile-history-table-wrap">
                          <table className="profile-history-table">
                            <thead>
                              <tr>
                                <th>Model</th>
                                <th>Integration</th>
                                <th>Status</th>
                                <th>Created</th>
                                <th>Actions</th>
                              </tr>
                            </thead>
                            <tbody>
                              {displayedFineTuningJobs.map((job, index) => {
                                const modelDisplay = extractFineTuningModelDisplayName(job) || '—';
                                const modelIdForConnector = extractFineTuningConfiguratorModelId(job);
                                const integrationId = String(job?.integration_id || '');
                                const integrationLabel =
                                  integrationNameById[integrationId] ||
                                  (integrationId ? `${integrationId.slice(0, 8)}...` : '—');
                                const statusRaw = String(job?.status || '').toLowerCase();
                                const canLoad =
                                  (statusRaw === 'succeeded' || statusRaw === 'completed' || statusRaw === 'partially_succeeded') &&
                                  Boolean(modelIdForConnector) &&
                                  Boolean(integrationId);
                                const canRetry = canRetryTrainingJob(job.status);
                                const jobId = resolveTrainingJobRecordId(job);
                                const isRetrying = isJobRetryUiRowActive(fineTuningRetryingRowKey, {
                                  kind: 'profile-table',
                                  jobId,
                                  index,
                                });
                                return (
                                  <tr key={job.id || `ft-${index}`}>
                                    <td title={modelIdForConnector && modelDisplay !== modelIdForConnector ? modelIdForConnector : undefined}>
                                      {modelDisplay}
                                    </td>
                                    <td title={integrationId || undefined}>{integrationLabel}</td>
                                    <td>{job.status || '—'}</td>
                                    <td>{formatDateShort(job.created_at || '')}</td>
                                    <td>
                                      <button
                                        type="button"
                                        className="view-steps-btn"
                                        onClick={() => openFineTuningJobDetails(job)}
                                        style={{
                                          marginRight: 8,
                                          minWidth: 160,
                                          height: 36,
                                          padding: '0 12px',
                                          display: 'inline-flex',
                                          alignItems: 'center',
                                          justifyContent: 'center',
                                          gap: 8,
                                        }}
                                        title="Open full fine-tuning job details"
                                      >
                                        <i className="fas fa-eye" aria-hidden /> Details
                                      </button>
                                      {canRetry && (
                                        <button
                                          type="button"
                                          className="view-steps-btn"
                                          onClick={() =>
                                            void handleRetryFineTuningJob(
                                              job,
                                              buildJobRetryUiRowKey({ kind: 'profile-table', jobId, index })
                                            )
                                          }
                                          disabled={isRetrying}
                                          aria-busy={isRetrying}
                                          style={{
                                            marginRight: 8,
                                            minWidth: 120,
                                            height: 36,
                                            padding: '0 12px',
                                            display: 'inline-flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            gap: 8,
                                            opacity: isRetrying ? 0.7 : 1,
                                          }}
                                          title="Create a new fine-tuning job from this one"
                                        >
                                          <i
                                            className={`fas ${isRetrying ? 'fa-spinner fa-spin' : 'fa-redo'}`}
                                            aria-hidden
                                          />
                                          {isRetrying ? 'Retrying…' : 'Retry'}
                                        </button>
                                      )}
                                      <button
                                        type="button"
                                        className="profile-view-btn"
                                        onClick={() => handleLoadFineTuningToConnector(job)}
                                        disabled={!canLoad}
                                        title={
                                          canLoad
                                            ? 'Add model to Connector and open integration'
                                            : 'Model is available after job finishes'
                                        }
                                        style={{
                                          background: canLoad ? '#FF8300' : '#C6E0F7',
                                          borderColor: canLoad ? '#FF8300' : '#C6E0F7',
                                          minWidth: 160,
                                          height: 36,
                                          padding: '0 12px',
                                          display: 'inline-flex',
                                          alignItems: 'center',
                                          justifyContent: 'center',
                                          gap: 8,
                                        }}
                                      >
                                        <i className="fas fa-upload" aria-hidden /> Load to Connector
                                      </button>
                                    </td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>
                        <div className="profile-history-cards">
                          {displayedFineTuningJobs.map((job, index) => {
                            const modelDisplay = extractFineTuningModelDisplayName(job) || '—';
                            const modelIdForConnector = extractFineTuningConfiguratorModelId(job);
                            const integrationId = String(job?.integration_id || '');
                            const integrationLabel =
                              integrationNameById[integrationId] ||
                              (integrationId ? `${integrationId.slice(0, 8)}...` : '—');
                            const statusRaw = String(job?.status || '').toLowerCase();
                            const canLoad =
                              (statusRaw === 'succeeded' || statusRaw === 'completed' || statusRaw === 'partially_succeeded') &&
                              Boolean(modelIdForConnector) &&
                              Boolean(integrationId);
                            const canRetry = canRetryTrainingJob(job.status);
                            const jobId = resolveTrainingJobRecordId(job);
                            const isRetrying = isJobRetryUiRowActive(fineTuningRetryingRowKey, {
                              kind: 'profile-card',
                              jobId,
                              index,
                            });
                            return (
                              <div key={job.id || `ft-card-${index}`} className="profile-history-card-item">
                                <div className="profile-history-card-header">
                                  <div className="profile-history-card-date">{formatDateShort(job.created_at || '')}</div>
                                </div>
                                <div className="profile-history-card-content">
                                  <div className="profile-history-card-field">
                                    <div className="profile-history-card-label">Model</div>
                                    <div
                                      className="profile-history-card-value"
                                      title={modelIdForConnector && modelDisplay !== modelIdForConnector ? modelIdForConnector : undefined}
                                    >
                                      {modelDisplay}
                                    </div>
                                  </div>
                                  <div className="profile-history-card-field">
                                    <div className="profile-history-card-label">Integration</div>
                                    <div className="profile-history-card-value" title={integrationId || undefined}>
                                      {integrationLabel}
                                    </div>
                                  </div>
                                  <div className="profile-history-card-field">
                                    <div className="profile-history-card-label">Status</div>
                                    <div className="profile-history-card-value">{job.status || '—'}</div>
                                  </div>
                                </div>
                                <div className="profile-history-card-actions">
                                  <button
                                    type="button"
                                    className="view-steps-btn"
                                    onClick={() => openFineTuningJobDetails(job)}
                                    style={{
                                      minWidth: 160,
                                      height: 36,
                                      padding: '0 12px',
                                      display: 'inline-flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      gap: 8,
                                    }}
                                    title="Open full fine-tuning job details"
                                  >
                                    <i className="fas fa-eye" aria-hidden /> Details
                                  </button>
                                  {canRetry ? (
                                    <button
                                      type="button"
                                      className="view-steps-btn"
                                      onClick={() =>
                                        void handleRetryFineTuningJob(
                                          job,
                                          buildJobRetryUiRowKey({ kind: 'profile-card', jobId, index })
                                        )
                                      }
                                      disabled={isRetrying}
                                      aria-busy={isRetrying}
                                      style={{
                                        minWidth: 120,
                                        height: 36,
                                        padding: '0 12px',
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        gap: 8,
                                        opacity: isRetrying ? 0.7 : 1,
                                      }}
                                      title="Create a new fine-tuning job from this one"
                                    >
                                      <i
                                        className={`fas ${isRetrying ? 'fa-spinner fa-spin' : 'fa-redo'}`}
                                        aria-hidden
                                      />
                                      {isRetrying ? 'Retrying…' : 'Retry'}
                                    </button>
                                  ) : null}
                                  <button
                                    type="button"
                                    className="profile-view-btn"
                                    onClick={() => handleLoadFineTuningToConnector(job)}
                                    disabled={!canLoad}
                                    title={
                                      canLoad
                                        ? 'Add model to Connector and open integration'
                                        : 'Model is available after job finishes'
                                    }
                                    style={{
                                      background: canLoad ? '#FF8300' : '#C6E0F7',
                                      borderColor: canLoad ? '#FF8300' : '#C6E0F7',
                                      minWidth: 160,
                                      height: 36,
                                      padding: '0 12px',
                                      display: 'inline-flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      gap: 8,
                                    }}
                                  >
                                    <i className="fas fa-upload" aria-hidden /> Load to Connector
                                  </button>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                        {fineTuningFiltersActive && fineTuningLoadingMore && (
                          <p
                            role="status"
                            style={{ margin: '16px 0 0', textAlign: 'center', color: '#5B7893', fontSize: 13 }}
                          >
                            Searching all jobs for matches…
                          </p>
                        )}
                        {fineTuningHasMore && !fineTuningFiltersActive && (
                          <div style={{ marginTop: 16, display: 'flex', justifyContent: 'center' }}>
                            <button
                              type="button"
                              className="history-category-tab active"
                              disabled={fineTuningLoadingMore || fineTuningLoading}
                              onClick={() => void loadFineTuningHistory({ append: true })}
                              style={{ minWidth: 200 }}
                            >
                              {fineTuningLoadingMore ? 'Loading…' : 'Load more jobs'}
                            </button>
                          </div>
                        )}
                      </>
                    )}
                  </div>
                </div>
              </div>
            ) : (
              <UsageTransactionsPage />
            )}
          </main>
        </div>
      </div>

      {/* Модальное окно для просмотра конфигурации */}
      {selectedFineTuningJob && (
        <div className="config-modal-overlay">
          <div className="config-modal" style={{ maxWidth: 980 }}>
            <div className="config-modal-header">
              <h3 className="config-modal-title">
                <i className="fas fa-graduation-cap"></i>
                Fine-tuning Job Details
              </h3>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                {canRetryTrainingJob(selectedFineTuningJob.status) && (() => {
                  const modalJobId = resolveTrainingJobRecordId(selectedFineTuningJob);
                  const isModalRetrying = isJobRetryUiRowActive(fineTuningRetryingRowKey, {
                    kind: 'profile-modal',
                    jobId: modalJobId,
                  });
                  return (
                  <button
                    type="button"
                    className="profile-view-btn"
                    onClick={() =>
                      void handleRetryFineTuningJob(
                        selectedFineTuningJob,
                        buildJobRetryUiRowKey({ kind: 'profile-modal', jobId: modalJobId })
                      )
                    }
                    disabled={isModalRetrying}
                    aria-busy={isModalRetrying}
                    style={{
                      background: '#FFFFFF',
                      borderColor: '#FF8300',
                      color: '#FF8300',
                      minHeight: 36,
                      padding: '0 14px',
                    }}
                    title="Create a new fine-tuning job from this one"
                  >
                    <i
                      className={`fas ${isModalRetrying ? 'fa-spinner fa-spin' : 'fa-redo'}`}
                      aria-hidden
                    />{' '}
                    {isModalRetrying ? 'Retrying…' : 'Retry'}
                  </button>
                  );
                })()}
                <button
                  className="config-modal-close"
                  onClick={() => {
                    setFineTuningRetryError(null);
                    setSelectedFineTuningJob(null);
                  }}
                >
                  <i className="fas fa-times"></i>
                </button>
              </div>
            </div>
            <div className="config-modal-content">
              {fineTuningRetryError ? (
                <div
                  style={{
                    marginBottom: 14,
                    padding: '10px 12px',
                    borderRadius: 8,
                    background: '#FFFFFF',
                    border: '1px solid #FFD9B3',
                    color: '#B85400',
                    fontSize: 13,
                  }}
                >
                  <i className="fas fa-exclamation-triangle" style={{ marginRight: 8 }} />
                  {fineTuningRetryError}
                </div>
              ) : null}
              {(() => {
                const job = selectedFineTuningJob;
                const groups = getFineTuningGroups(job);
                return (
                  <>
                    <div className="step-params" style={{ marginBottom: 16 }}>
                      <div className="step-param"><label>Job ID</label><span>{job.id || '—'}</span></div>
                      <div className="step-param"><label>Status</label><span>{job.status || '—'}</span></div>
                      {resolveFineTuningJobProgressMetrics(job.status, job.progress_percent).show ? (
                        <div className="step-param" style={{ gridColumn: '1 / -1' }}>
                          <FineTuningJobProgress status={job.status} progressPercent={job.progress_percent} />
                        </div>
                      ) : null}
                      <div className="step-param"><label>Integration</label><span>{job.integration_id || '—'}</span></div>
                      <div className="step-param"><label>Dataset Source</label><span>{(job as any).dataset_source || '—'}</span></div>
                      <div className="step-param"><label>Created</label><span>{formatDateShort(job.created_at || '')}</span></div>
                      <div className="step-param"><label>Completed</label><span>{formatDateShort(job.completed_at || '')}</span></div>
                    </div>
                    {(job.training_period_from || job.training_period_to) && (
                      <div className="step-prompt" style={{ marginBottom: 14 }}>
                        <label>Training period</label>
                        <div className="step-prompt-text">
                          {formatDateShort(job.training_period_from || '')} - {formatDateShort(job.training_period_to || '')}
                        </div>
                      </div>
                    )}
                    {(job as any).error && (
                      <div
                        style={{
                          marginBottom: 14,
                          padding: '10px 12px',
                          borderRadius: 8,
                          background: '#FFFFFF',
                          border: '1px solid #FFD9B3',
                          color: '#B85400',
                          fontSize: 13,
                        }}
                      >
                        <i className="fas fa-exclamation-triangle" style={{ marginRight: 8 }} />
                        {formatFineTuningDatasetFormatErrorMessage(String((job as any).error))}
                      </div>
                    )}
                    {groups.length > 0 && (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                        <label style={{ fontSize: 13, fontWeight: 600, color: '#4A6683' }}>Groups ({groups.length})</label>
                        {groups.map((g: any, idx: number) => (
                          <div key={`${g?.model_name || g?.base_model || 'group'}-${idx}`} style={{ border: '1px solid #E3F0FB', borderRadius: 8, padding: 12 }}>
                            <div className="step-params">
                              <div className="step-param"><label>Model Name</label><span>{g?.model_name || '—'}</span></div>
                              <div className="step-param"><label>Base Model</label><span>{g?.base_model_label || g?.base_model || '—'}</span></div>
                              <div className="step-param"><label>Status</label><span>{g?.status || '—'}</span></div>
                              <div className="step-param"><label>Raw Tokens</label><span>{typeof g?.dataset_tokens_raw === 'number' ? g.dataset_tokens_raw.toLocaleString() : '—'}</span></div>
                              <div className="step-param"><label>Prepared Tokens</label><span>{typeof g?.dataset_tokens_prepared === 'number' ? g.dataset_tokens_prepared.toLocaleString() : '—'}</span></div>
                            </div>
                            {g?.error && (
                              <div style={{ marginTop: 8, color: '#B85400', fontSize: 12 }}>
                                <i className="fas fa-exclamation-circle" style={{ marginRight: 6 }} />
                                {formatFineTuningDatasetFormatErrorMessage(String(g.error))}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </>
                );
              })()}
            </div>
          </div>
        </div>
      )}

      {/* Модальное окно для просмотра конфигурации */}
      {showConfigModal && selectedConfig && (
        <div className="config-modal-overlay">
          <div className="config-modal">
            <div className="config-modal-header">
              <h3 className="config-modal-title">
                <i className="fas fa-cog"></i>
                Initial Configuration
              </h3>
              <button 
                className="config-modal-close"
                onClick={() => setShowConfigModal(false)}
              >
                <i className="fas fa-times"></i>
              </button>
            </div>
            <div className="config-modal-content">
              {/* Multi-agent конфигурация */}
              {(() => {
                // Если нет master-agent в config, но есть step 0, используем его данные
                const step0 = selectedConfigSteps?.find(s => s.step_number === 0);
                const step0Master = extractCoordinatorFromData(step0?.data);
                const step0Workers = extractWorkersFromData(step0?.data);
                
                // Используем config или данные из step 0
                const masterAgent = (extractCoordinatorFromConfig(selectedConfig) || step0Master) as any;
                const workerAgents = ((extractWorkersFromConfig(selectedConfig).length > 0
                  ? extractWorkersFromConfig(selectedConfig)
                  : step0Workers || []) as any[]);
                
                if (!masterAgent) {
                  return (
                    <div className="history-legacy-notice" role="note">
                      <i className="fas fa-archive" aria-hidden />
                      <div>
                        <strong>Coordinator data not in this snapshot</strong>
                        <p>
                          This record has no master/coordinator block in the saved configuration. Check the sections below,
                          or it may be a legacy run with incomplete metadata.
                        </p>
                      </div>
                    </div>
                  );
                }
                
                return (
                <>
                  {/* Coordinator Section */}
                  <div className="agent-section coordinator-section">
                    <div 
                      className="agent-section-header"
                      onClick={() => toggleAgent('config-coordinator')}
                      style={{ cursor: 'pointer' }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <i className={`fas fa-chevron-${expandedAgents.has('config-coordinator') ? 'down' : 'right'}`} style={{ 
                          fontSize: '11px', 
                          color: 'var(--accent-blue)',
                          transition: 'transform 0.2s ease'
                        }}></i>
                        <i className="fas fa-crown" style={{ color: '#FF8300', fontSize: '14px' }}></i>
                        <span className="agent-section-title">Coordinator (Master Agent)</span>
                      </div>
                    </div>
                    {expandedAgents.has('config-coordinator') && (
                      <div className="agent-section-content">
                        <div className="step-prompt">
                          <label>Name</label>
                          <div className="step-prompt-text">
                            {masterAgent.name?.trim() ? masterAgent.name : <HistoryFieldEmpty>Unnamed</HistoryFieldEmpty>}
                          </div>
                        </div>
                        <div className="step-prompt">
                          <label>Prompt</label>
                          <div
                            className={`step-prompt-text formatted-text${!(masterAgent.prompt && String(masterAgent.prompt).trim()) ? ' step-prompt-text--empty' : ''}`}
                          >
                            {masterAgent.prompt && String(masterAgent.prompt).trim() ? (
                              masterAgent.prompt
                            ) : (
                              <HistoryFieldEmpty>No prompt stored</HistoryFieldEmpty>
                            )}
                          </div>
                        </div>
                        {masterAgent.description && (
                          <div className="step-prompt">
                            <label>Description</label>
                            <div className="step-prompt-text formatted-text">{masterAgent.description}</div>
                          </div>
                        )}
                        <div className="step-params">
                          <div className="step-param">
                            <label>Model</label>
                            <span>
                              {masterAgent?.model_name || masterAgent?.model ? (
                                masterAgent.model_name || masterAgent.model
                              ) : (
                                <HistoryFieldEmpty>—</HistoryFieldEmpty>
                              )}
                            </span>
                          </div>
                          <div className="step-param">
                            <label>Reasoning</label>
                            <span>{historyReasoningDisplay(masterAgent)}</span>
                          </div>
                          <div className="step-param">
                            <label>Temperature</label>
                            <span>{historyTemperatureDisplay(masterAgent)}</span>
                          </div>
                          <div className="step-param">
                            <label>Use RAG</label>
                            <span>{masterAgent?.use_rag ? 'Yes' : 'No'}</span>
                          </div>
                          <div className="step-param">
                            <label>Use LTM</label>
                            <span>{masterAgent?.use_ltm ? 'Yes' : 'No'}</span>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                  
                  {/* Worker agents: synchronous / asynchronous (Connector-style) */}
                  {workerAgents && workerAgents.length > 0 && (
                    <HistoryWorkerModeSections
                      workers={workerAgents}
                      expandedAgents={expandedAgents}
                      toggleAgent={toggleAgent}
                      groupIdPrefix="config"
                      variant="initial"
                    />
                  )}
                  
                  {/* General Settings */}
                  <div className="agent-section general-settings-section">
                      <div 
                        className="agent-section-header"
                        onClick={() => toggleAgent('config-general-settings')}
                        style={{ cursor: 'pointer' }}
                      >
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                          <i className={`fas fa-chevron-${expandedAgents.has('config-general-settings') ? 'down' : 'right'}`} style={{ 
                            fontSize: '11px', 
                            color: 'var(--accent-blue)',
                            transition: 'transform 0.2s ease'
                          }}></i>
                          <i className="fas fa-cog" style={{ color: 'var(--accent-blue)', fontSize: '14px' }}></i>
                          <span className="agent-section-title">General Settings</span>
                        </div>
                      </div>
                      {expandedAgents.has('config-general-settings') && (
                        <div className="agent-section-content">
                          <div className="step-params">
                            <div className="step-param">
                              <label>Dataset</label>
                              <span
                                className="profile-history-dataset-name"
                                title={formatOptimizationDatasetDisplay(selectedConfig.dataset_name)}
                              >
                                {formatOptimizationDatasetDisplay(selectedConfig.dataset_name) ? (
                                  formatOptimizationDatasetDisplay(selectedConfig.dataset_name)
                                ) : (
                                  <HistoryFieldEmpty>—</HistoryFieldEmpty>
                                )}
                              </span>
                            </div>
                            <div className="step-param">
                              <label>Iterations</label>
                              <span>{renderHistoryIterations(selectedConfig.iterations)}</span>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                </>
                );
              })()}
              
              {/* Integrations формат (coordinator + workers + metaPrompts) */}
              {!extractCoordinatorFromConfig(selectedConfig) && (() => {
                // Используем step 0 из steps, если config пустой
                const step0 = selectedConfigSteps?.find(s => s.step_number === 0);
                
                // Для integrations может быть coordinator и workers в config или step.data
                // Проверяем сначала config, потом step0.data
                const configCoordinator = (selectedConfig as any)?.coordinator;
                const step0Coordinator = extractCoordinatorFromData(step0?.data);
                const coordinator = configCoordinator || step0Coordinator;
                
                const configWorkers = (selectedConfig as any)?.workers;
                const step0Workers = extractWorkersFromData(step0?.data);
                const workers = configWorkers || step0Workers || [];
                
                const configMetaPrompts = (selectedConfig as any)?.metaPrompts;
                const step0MetaPrompts = extractMetaPromptsFromData(step0?.data);
                const metaPrompts = configMetaPrompts || step0MetaPrompts;
                
                // Fallback для старого формата (single-agent)
                const step0Prompt = step0?.prompt || '';
                const step0Model = step0?.model_name || '';
                const step0Temp = step0?.temperature;
                const initialPrompt = selectedConfig.initial_prompt || step0Prompt || '';
                const modelName = selectedConfig.model_name || step0Model || '';
                const hasExplicitTemperature =
                  (selectedConfig.temperature !== undefined && selectedConfig.temperature !== null) ||
                  (step0Temp !== undefined && step0Temp !== null);
                const temperatureValue = hasExplicitTemperature
                  ? (selectedConfig.temperature !== undefined && selectedConfig.temperature !== null
                    ? selectedConfig.temperature
                    : step0Temp)
                  : undefined;
                
                // Если есть coordinator - это integrations формат
                if (coordinator) {
                  return (
                    <>
                      {/* Coordinator */}
                      <div className="agent-section coordinator-section">
                        <div 
                          className="agent-section-header"
                          onClick={() => toggleAgent('config-coordinator')}
                          style={{ cursor: 'pointer' }}
                        >
                          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                            <i className={`fas fa-chevron-${expandedAgents.has('config-coordinator') ? 'down' : 'right'}`} style={{ 
                              fontSize: '11px', 
                              color: 'var(--accent-blue)',
                              transition: 'transform 0.2s ease'
                            }}></i>
                            <i className="fas fa-crown" style={{ color: '#FF8300', fontSize: '14px' }}></i>
                            <span className="agent-section-title">Coordinator</span>
                          </div>
                        </div>
                        {expandedAgents.has('config-coordinator') && (
                          <div className="agent-section-content">
                            <div className="step-prompt">
                              <label>Name</label>
                              <div className="step-prompt-text">
                                {coordinator.name?.trim() ? coordinator.name : <HistoryFieldEmpty>Unnamed</HistoryFieldEmpty>}
                              </div>
                            </div>
                            <div className="step-prompt">
                              <label>Prompt</label>
                              <div
                                className={`step-prompt-text formatted-text${!(coordinator.prompt && String(coordinator.prompt).trim()) ? ' step-prompt-text--empty' : ''}`}
                              >
                                {coordinator.prompt && String(coordinator.prompt).trim() ? (
                                  coordinator.prompt
                                ) : (
                                  <HistoryFieldEmpty>No prompt stored</HistoryFieldEmpty>
                                )}
                              </div>
                            </div>
                            <div className="step-params">
                              <div className="step-param">
                                <label>Model</label>
                                <span>
                                  {coordinator.model || coordinator.model_name ? (
                                    coordinator.model || coordinator.model_name
                                  ) : (
                                    <HistoryFieldEmpty>—</HistoryFieldEmpty>
                                  )}
                                </span>
                              </div>
                              <div className="step-param">
                                <label>Reasoning</label>
                                <span>{historyReasoningDisplay(coordinator)}</span>
                              </div>
                              <div className="step-param">
                                <label>Temperature</label>
                                <span>{historyTemperatureDisplay(coordinator)}</span>
                              </div>
                              <div className="step-param">
                                <label>Use RAG</label>
                                <span>
                                  {coordinator.use_rag === true
                                    ? 'Yes'
                                    : coordinator.use_rag === false
                                      ? 'No'
                                      : <HistoryFieldEmpty>—</HistoryFieldEmpty>}
                                </span>
                              </div>
                              <div className="step-param">
                                <label>Use LTM</label>
                                <span>
                                  {coordinator.use_ltm === true
                                    ? 'Yes'
                                    : coordinator.use_ltm === false
                                      ? 'No'
                                      : <HistoryFieldEmpty>—</HistoryFieldEmpty>}
                                </span>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                      
                      {/* Workers: synchronous / asynchronous (Connector-style) */}
                      {workers && workers.length > 0 && (
                        <HistoryWorkerModeSections
                          workers={workers}
                          expandedAgents={expandedAgents}
                          toggleAgent={toggleAgent}
                          groupIdPrefix="config"
                          variant="integrations"
                        />
                      )}
                      
                      {/* Meta Prompts */}
                      <div className="agent-section meta-prompts-section">
                          <div 
                            className="agent-section-header"
                            onClick={() => toggleAgent('config-meta-prompts')}
                            style={{ cursor: 'pointer' }}
                          >
                            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                              <i className={`fas fa-chevron-${expandedAgents.has('config-meta-prompts') ? 'down' : 'right'}`} style={{ 
                                fontSize: '11px', 
                                color: 'var(--accent-blue)',
                                transition: 'transform 0.2s ease'
                              }}></i>
                              <i className="fas fa-file-alt" style={{ color: '#FF8300', fontSize: '14px' }}></i>
                              <span className="agent-section-title">Meta Prompts</span>
                            </div>
                          </div>
                          {expandedAgents.has('config-meta-prompts') && (
                            <div className="agent-section-content">
                              {metaPrompts && Object.keys(metaPrompts).length > 0 ? (
                                Object.entries(metaPrompts).map(([key, value]: [string, any]) => {
                                  const str = value != null && String(value).trim() !== '' ? String(value) : '';
                                  return (
                                    <div key={key} className="step-prompt">
                                      <label>{key}</label>
                                      <div
                                        className={`step-prompt-text formatted-text${!str ? ' step-prompt-text--empty' : ''}`}
                                      >
                                        {str || <HistoryFieldEmpty>Empty</HistoryFieldEmpty>}
                                      </div>
                                    </div>
                                  );
                                })
                              ) : (
                                <p className="history-inline-hint">No meta prompts were stored for this run.</p>
                              )}
                            </div>
                          )}
                        </div>
                      
                      {/* General Settings */}
                      <div className="agent-section general-settings-section">
                          <div 
                            className="agent-section-header"
                            onClick={() => toggleAgent('config-general-settings')}
                            style={{ cursor: 'pointer' }}
                          >
                            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                              <i className={`fas fa-chevron-${expandedAgents.has('config-general-settings') ? 'down' : 'right'}`} style={{ 
                                fontSize: '11px', 
                                color: 'var(--accent-blue)',
                                transition: 'transform 0.2s ease'
                              }}></i>
                              <i className="fas fa-cog" style={{ color: 'var(--accent-blue)', fontSize: '14px' }}></i>
                              <span className="agent-section-title">General Settings</span>
                            </div>
                          </div>
                          {expandedAgents.has('config-general-settings') && (
                            <div className="agent-section-content">
                              <div className="step-params">
                                <div className="step-param">
                                  <label>Dataset</label>
                                  <span
                                    className="profile-history-dataset-name"
                                    title={formatOptimizationDatasetDisplay(selectedConfig.dataset_name)}
                                  >
                                    {formatOptimizationDatasetDisplay(selectedConfig.dataset_name) ? (
                                      formatOptimizationDatasetDisplay(selectedConfig.dataset_name)
                                    ) : (
                                      <HistoryFieldEmpty>—</HistoryFieldEmpty>
                                    )}
                                  </span>
                                </div>
                                <div className="step-param">
                                  <label>Iterations</label>
                                  <span>{renderHistoryIterations(selectedConfig.iterations)}</span>
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                    </>
                  );
                }
                
                // Старый формат (single-agent)
                if (!initialPrompt && !step0) {
                  return (
                    <div className="history-legacy-notice" role="note">
                      <i className="fas fa-archive" aria-hidden />
                      <div>
                        <strong>No initial configuration in this record</strong>
                        <p>
                          There is no saved initial prompt or step&nbsp;0 payload. This may be a legacy or incomplete
                          optimization entry.
                        </p>
                      </div>
                    </div>
                  );
                }
                
                return (
                  <div className="step-item">
                    <div className="step-header">
                      <span className="step-number">Initial Configuration</span>
                      <div className="step-indicator">
                        <div className="step-dot"></div>
                      </div>
                    </div>
                    <div className="step-content">
                      <div className="step-prompt">
                        <label>Initial Prompt</label>
                        <div
                          className={`step-prompt-text${!String(initialPrompt || '').trim() ? ' step-prompt-text--empty' : ''}`}
                        >
                          {String(initialPrompt || '').trim() ? (
                            initialPrompt
                          ) : (
                            <HistoryFieldEmpty>No prompt stored</HistoryFieldEmpty>
                          )}
                        </div>
                      </div>
                      <div className="step-params">
                        <div className="step-param">
                          <label>Model</label>
                          <span>
                            {String(modelName || '').trim() ? modelName : <HistoryFieldEmpty>—</HistoryFieldEmpty>}
                          </span>
                        </div>
                        <div className="step-param">
                          <label>Reasoning</label>
                          <span>
                            {historyReasoningDisplay({
                              model: modelName,
                              models_reasoning:
                                (selectedConfig as { models_reasoning?: unknown })?.models_reasoning ??
                                (step0?.data as { models_reasoning?: unknown } | undefined)?.models_reasoning,
                            })}
                          </span>
                        </div>
                        <div className="step-param">
                          <label>Temperature</label>
                          <span>
                            {historyTemperatureDisplay({
                              model: modelName,
                              models_reasoning:
                                (selectedConfig as { models_reasoning?: unknown })?.models_reasoning ??
                                (step0?.data as { models_reasoning?: unknown } | undefined)?.models_reasoning,
                              temperature: temperatureValue,
                            })}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })()}
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, padding: '16px 20px', borderTop: '1px solid #E3F0FB' }}>
              {historyCategory === 'integrations' ? (
                <button
                  className="export-step-btn"
                  onClick={handleLoadInitialConfigToConnector}
                  title="Load full initial configuration into Connector configurator"
                  style={{ padding: '8px 14px', fontSize: '12px', background: '#FF8300', color: '#FFFFFF', border: 'none', borderRadius: '8px', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: '10px' }}
                >
                  <i className="fas fa-upload" aria-hidden />
                  Load initial config to Connector
                </button>
              ) : (
                <button
                  className="export-step-btn"
                  onClick={handleLoadInitialConfigToMultiAgent}
                  title="Load full initial configuration into Multi-Agent optimization"
                  style={{ padding: '8px 14px', fontSize: '12px', background: '#0099FA', color: '#FFFFFF', border: 'none', borderRadius: '8px', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: '10px' }}
                >
                  <i className="fas fa-robot" aria-hidden />
                  Load initial config to Multi-Agent
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Модальное окно для просмотра шагов */}
      {showStepsModal && (
        <div className="steps-modal-overlay">
          <div className="steps-modal">
            <div className="steps-modal-header">
              <h3 className="steps-modal-title">
                <i className="fas fa-robot"></i>
                Optimization Steps
              </h3>
              <button 
                className="steps-modal-close"
                onClick={() => setShowStepsModal(false)}
              >
                <i className="fas fa-times"></i>
              </button>
            </div>
            <div className="steps-modal-content">
              {(() => {
                // Фильтруем step 0, так как он отображается в Initial Configuration
                const optimizationSteps = selectedSteps.filter(step => step.step_number > 0);
                return optimizationSteps.length === 0 ? (
                  <div className="history-steps-empty" role="status">
                    <i className="fas fa-layer-group" aria-hidden />
                    <p className="history-steps-empty__title">No optimization steps</p>
                    <p className="history-steps-empty__text">
                      For this run, only the initial configuration may have been saved, or the job did not produce
                      further steps. Open <strong>Initial configuration</strong> from the history list if available.
                    </p>
                  </div>
                ) : (
                  <div className="steps-list">
                    {optimizationSteps.map((step, stepIndex) => {
                    const stepMasterAgent = extractCoordinatorFromData(step.data);
                    const hasMasterAgent = !!stepMasterAgent;
                    const workerAgents = extractWorkersFromData(step.data);
                    const metaPrompts = extractMetaPromptsFromData(step.data);
                    
                    return (
                      <div key={step.step_number} className={`step-item ${selectedStepId === step.step_number ? 'selected-step' : ''}`}>
                        <div className="step-header" style={{ position: 'relative' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flex: 1, minWidth: 0 }}>
                            <span className="step-number">
                              Step {step.step_number}
                              {selectedStepId === step.step_number && (
                                <span className="selected-indicator">Selected</span>
                              )}
                            </span>
                            {hasMasterAgent && (
                              <span className="step-badge" style={{ 
                                fontSize: '11px', 
                                padding: '4px 10px',
                                background: 'rgba(0, 153, 250, 0.1)',
                                borderRadius: '12px',
                                color: 'var(--accent-blue)',
                                fontWeight: 600,
                                flexShrink: 0
                              }}>
                                Multi-Agent
                              </span>
                            )}
                          </div>
                          {hasMasterAgent && (
                            <button
                              className="export-step-btn"
                              onClick={(e) => {
                                e.stopPropagation();
                                e.preventDefault();
                                if (historyCategory === 'integrations') {
                                  handleLoadStepToConnector(step);
                                } else {
                                  handleLoadStepToMultiAgent(step);
                                }
                                setShowStepsModal(false);
                              }}
                              onMouseDown={(e) => {
                                e.stopPropagation();
                              }}
                              title={historyCategory === 'integrations' ? 'Load this step into Connector configurator' : 'Load this step into Multi-Agent optimization'}
                              style={{
                                marginLeft: '12px',
                                padding: '6px 12px',
                                fontSize: '12px',
                                background: '#FF8300',
                                color: 'white',
                                border: 'none',
                                borderRadius: '8px',
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '10px',
                                transition: 'all 0.2s ease',
                                position: 'relative',
                                zIndex: 100,
                                pointerEvents: 'auto',
                                flexShrink: 0
                              }}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.background = '#FF8300';
                                e.currentTarget.style.transform = 'scale(1.05)';
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.background = '#FF8300';
                                e.currentTarget.style.transform = 'scale(1)';
                              }}
                            >
                              <i className="fas fa-upload" aria-hidden />
                              {historyCategory === 'integrations' ? 'Load to Connector' : 'Load to Multi-Agent'}
                            </button>
                          )}
                          <div className="step-indicator" style={{ position: 'relative', zIndex: 1 }}>
                            <div className="step-dot"></div>
                          </div>
                        </div>
                        <div className="step-content">
                            {/* Multi-agent конфигурация из step.data */}
                            {hasMasterAgent && (
                              <>
                                {/* Coordinator Section */}
                                <div className="agent-section coordinator-section">
                                  <div 
                                    className="agent-section-header"
                                    onClick={() => toggleAgent(`step-${step.step_number}-coordinator`)}
                                    style={{ cursor: 'pointer' }}
                                  >
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                      <i className={`fas fa-chevron-${expandedAgents.has(`step-${step.step_number}-coordinator`) ? 'down' : 'right'}`} style={{ 
                                        fontSize: '11px', 
                                        color: 'var(--accent-blue)',
                                        transition: 'transform 0.2s ease'
                                      }}></i>
                                      <i className="fas fa-crown" style={{ color: '#FF8300', fontSize: '14px' }}></i>
                                      <span className="agent-section-title">Coordinator (Master Agent)</span>
                                    </div>
                                  </div>
                                  {expandedAgents.has(`step-${step.step_number}-coordinator`) && stepMasterAgent && (
                                    <div className="agent-section-content">
                                      <div className="agent-info">
                                        <div className="info-row">
                                          <span className="info-label">Name:</span>
                                          <span className="info-value">
                                            {stepMasterAgent?.name?.trim() ? stepMasterAgent.name : <HistoryFieldEmpty>Unnamed</HistoryFieldEmpty>}
                                          </span>
                                        </div>
                                        <div className="step-prompt">
                                          <label>Prompt</label>
                                          <div
                                            className={`step-prompt-text formatted-text${!(stepMasterAgent?.prompt && String(stepMasterAgent.prompt).trim()) ? ' step-prompt-text--empty' : ''}`}
                                          >
                                            {stepMasterAgent?.prompt && String(stepMasterAgent.prompt).trim() ? (
                                              stepMasterAgent.prompt
                                            ) : (
                                              <HistoryFieldEmpty>No prompt stored</HistoryFieldEmpty>
                                            )}
                                          </div>
                                        </div>
                                        {stepMasterAgent?.description && (
                                          <div className="step-prompt">
                                            <label>Description</label>
                                            <div className="step-prompt-text formatted-text">{stepMasterAgent.description}</div>
                                          </div>
                                        )}
                                        <div className="step-params">
                                          <div className="step-param">
                                            <label>Model</label>
                                            <span>
                                              {stepMasterAgent?.model_name || stepMasterAgent?.model ? (
                                                stepMasterAgent.model_name || stepMasterAgent.model
                                              ) : (
                                                <HistoryFieldEmpty>—</HistoryFieldEmpty>
                                              )}
                                            </span>
                                          </div>
                                          <div className="step-param">
                                            <label>Reasoning</label>
                                            <span>{historyReasoningDisplay(stepMasterAgent)}</span>
                                          </div>
                                          <div className="step-param">
                                            <label>Temperature</label>
                                            <span>{historyTemperatureDisplay(stepMasterAgent)}</span>
                                          </div>
                                          <div className="step-param">
                                            <label>Use RAG</label>
                                            <span>
                                              {stepMasterAgent?.use_rag === true
                                                ? 'Yes'
                                                : stepMasterAgent?.use_rag === false
                                                  ? 'No'
                                                  : <HistoryFieldEmpty>—</HistoryFieldEmpty>}
                                            </span>
                                          </div>
                                          <div className="step-param">
                                            <label>Use LTM</label>
                                            <span>
                                              {stepMasterAgent?.use_ltm === true
                                                ? 'Yes'
                                                : stepMasterAgent?.use_ltm === false
                                                  ? 'No'
                                                  : <HistoryFieldEmpty>—</HistoryFieldEmpty>}
                                            </span>
                                          </div>
                                        </div>
                                      </div>
                                    </div>
                                  )}
                                </div>
                                
                                {/* Worker agents: synchronous / asynchronous (Connector-style) */}
                                {workerAgents.length > 0 && (
                                  <HistoryWorkerModeSections
                                    workers={workerAgents}
                                    expandedAgents={expandedAgents}
                                    toggleAgent={toggleAgent}
                                    groupIdPrefix={`step-${step.step_number}`}
                                    variant="steps"
                                  />
                                )}
                                
                                {/* Meta Prompts Section */}
                                <div className="agent-section meta-prompts-section">
                                    <div 
                                      className="agent-section-header"
                                      onClick={() => toggleAgent(`step-${step.step_number}-meta-prompts`)}
                                      style={{ cursor: 'pointer' }}
                                    >
                                      <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                        <i className={`fas fa-chevron-${expandedAgents.has(`step-${step.step_number}-meta-prompts`) ? 'down' : 'right'}`} style={{ 
                                          fontSize: '11px', 
                                          color: 'var(--accent-blue)',
                                          transition: 'transform 0.2s ease'
                                        }}></i>
                                        <i className="fas fa-file-alt" style={{ color: '#FF8300', fontSize: '14px' }}></i>
                                        <span className="agent-section-title">Meta Prompts</span>
                                      </div>
                                    </div>
                                    {expandedAgents.has(`step-${step.step_number}-meta-prompts`) && (
                                      <div className="agent-section-content">
                                        {metaPrompts && Object.keys(metaPrompts).length > 0 ? (
                                          Object.entries(metaPrompts).map(([key, value]: [string, any]) => {
                                            const str = value != null && String(value).trim() !== '' ? String(value) : '';
                                            return (
                                              <div key={key} className="step-prompt">
                                                <label>{key}</label>
                                                <div
                                                  className={`step-prompt-text formatted-text${!str ? ' step-prompt-text--empty' : ''}`}
                                                >
                                                  {str || <HistoryFieldEmpty>Empty</HistoryFieldEmpty>}
                                                </div>
                                              </div>
                                            );
                                          })
                                        ) : (
                                          <p className="history-inline-hint">No meta prompts in this step payload.</p>
                                        )}
                                      </div>
                                    )}
                                  </div>
                              </>
                            )}
                            
                            {/* Fallback для single-agent или если нет data */}
                            {!hasMasterAgent && (
                              <>
                                <div className="agent-section single-agent-section">
                                  <div className="step-prompt">
                                    <label>Prompt</label>
                                    <div
                                      className={`step-prompt-text formatted-text${!(step.prompt && String(step.prompt).trim()) ? ' step-prompt-text--empty' : ''}`}
                                    >
                                      {step.prompt && String(step.prompt).trim() ? (
                                        step.prompt
                                      ) : (
                                        <HistoryFieldEmpty>No prompt stored</HistoryFieldEmpty>
                                      )}
                                    </div>
                                  </div>
                                  <div className="step-params">
                                    <div className="step-param">
                                      <label>Model</label>
                                      <span>
                                        {step.model_name?.trim() ? step.model_name : <HistoryFieldEmpty>—</HistoryFieldEmpty>}
                                      </span>
                                    </div>
                                    <div className="step-param">
                                      <label>Reasoning</label>
                                      <span>
                                        {historyReasoningDisplay({
                                          model: step.model_name,
                                          models_reasoning: (step as OptimizationStep & { models_reasoning?: unknown })
                                            .models_reasoning,
                                        })}
                                      </span>
                                    </div>
                                    <div className="step-param">
                                      <label>Temperature</label>
                                      <span>
                                        {historyTemperatureDisplay({
                                          model: step.model_name,
                                          models_reasoning: (step as OptimizationStep & { models_reasoning?: unknown })
                                            .models_reasoning,
                                          temperature: step.temperature,
                                        })}
                                      </span>
                                    </div>
                                  </div>
                                </div>
                                
                                {/* Meta Prompts для single-agent */}
                                <div className="agent-section meta-prompts-section">
                                    <div 
                                      className="agent-section-header"
                                      onClick={() => toggleAgent(`step-${step.step_number}-meta-prompts-sa`)}
                                      style={{ cursor: 'pointer' }}
                                    >
                                      <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                        <i className={`fas fa-chevron-${expandedAgents.has(`step-${step.step_number}-meta-prompts-sa`) ? 'down' : 'right'}`} style={{ 
                                          fontSize: '11px', 
                                          color: 'var(--accent-blue)',
                                          transition: 'transform 0.2s ease'
                                        }}></i>
                                        <i className="fas fa-file-alt" style={{ color: '#FF8300', fontSize: '14px' }}></i>
                                        <span className="agent-section-title">Meta Prompts</span>
                                      </div>
                                    </div>
                                    {expandedAgents.has(`step-${step.step_number}-meta-prompts-sa`) && (
                                      <div className="agent-section-content">
                                        {metaPrompts && Object.keys(metaPrompts).length > 0 ? (
                                          Object.entries(metaPrompts).map(([key, value]: [string, any]) => {
                                            const str = value != null && String(value).trim() !== '' ? String(value) : '';
                                            return (
                                              <div key={key} className="step-prompt">
                                                <label>{key}</label>
                                                <div
                                                  className={`step-prompt-text formatted-text${!str ? ' step-prompt-text--empty' : ''}`}
                                                >
                                                  {str || <HistoryFieldEmpty>Empty</HistoryFieldEmpty>}
                                                </div>
                                              </div>
                                            );
                                          })
                                        ) : (
                                          <p className="history-inline-hint">No meta prompts in this step payload.</p>
                                        )}
                                      </div>
                                    )}
                                  </div>
                              </>
                            )}
                          </div>
                      </div>
                    );
                  })}
                </div>
                );
              })()}
            </div>
          </div>
        </div>
      )}
      {showProfileOnboarding && (
        <ProfileOnboarding
          isOpen
          onClose={() => setShowProfileOnboarding(false)}
          onTabChange={handleProfileOnboardingTab}
        />
      )}
      <ConfirmModal
        isOpen={deleteModelConfirmTarget !== null}
        title="Delete fine-tuned model"
        variant="danger"
        message={
          deleteModelConfirmTarget ? (
            <>
              <p className="confirm-modal-line">
                Delete fine-tuned model{' '}
                <strong className="confirm-modal-filename">
                  “{deleteModelConfirmTarget.model_name ||
                    deleteModelConfirmTarget.fine_tuned_model_id ||
                    deleteModelConfirmTarget.agent_name ||
                    'this model'}”
                </strong>
                ?
              </p>
              <p className="confirm-modal-line confirm-modal-line--muted">This cannot be undone.</p>
            </>
          ) : (
            ''
          )
        }
        confirmText="Delete model"
        cancelText="Cancel"
        isConfirmLoading={Boolean(deletingTrainingAgentModelId)}
        onConfirm={() => void confirmDeleteTrainingAgentModel()}
        onCancel={() => {
          if (!deletingTrainingAgentModelId) setDeleteModelConfirmTarget(null);
        }}
      />
    </>
  );
}
